import { MarkerType, type Edge, type Node } from "@xyflow/react";
import {
    isSchedulerDraftWeekdayEmptyShell,
    mergeDaysweekSchedulerDaySources,
    normalizeIvrSchedulerScheduleKind,
    parseIvrElementDraftData,
} from "./ivrElementDraftData";
import {
    IVR_MENU_DTMF_KEY_ORDER,
    IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT,
    IVR_SCHEDULER_SIBLING_GAP_PX,
    IVR_TREE_TRUNK_TO_BUS_GAP,
    IVR_TREE_BUS_TO_CHILD_DROP_PX,
    IVR_TREE_PARENT_TO_CHILD_GAP,
    ivrSchedulerSlotRowY,
    ivrSchedulerDayFirstSlotY,
    ivrSchedulerDaySlotRowLayout,
    ivrSchedulerAdvancedColumnWidth,
    IVR_NODE_WIDTH,
    IVR_START_ABOVE_ENTRY_GAP,
    IVR_MENU_DTMF_GAP_PX,
    IVR_PLAY_CHAIN_VERTICAL_GAP,
    IVR_MENU_DTMF_VERTICAL_GAP,
    ivrAgentHunterQueueChildY,
    IVR_AGENT_HUNTER_QUEUE_VERTICAL_GAP,
    ivrMenuRetriesBranchLayout,
    ivrEstimateMenuChainHeight,
    sortIvrMenuDtmfKeys,
} from "./ivrSchemeConstants";

export type HydrationBlockData = {
    label: string;
    shape: "circle" | "diamond" | "triangle" | "square";
    icon?: string;
    muted?: boolean;
    accentColor?: string;
    outlineDashed?: boolean;
    blockConfig?: Record<string, unknown>;
    isGenerated?: boolean;
    configParentId?: string;
    configSourceNodeId?: string;
};

export type IvrApiAudio = {
    audioId: number;
    audioName: string;
    groupId?: number | null;
    groupName?: string;
};

export type IvrApiElement = {
    flowId?: number;
    elementId: number;
    name: string | null;
    elementType: string;
    audios: IvrApiAudio[] | null;
    elementData: unknown;
    draftData?: string | null;
    minDigits?: number | null;
    maxDigits?: number | null;
    terminators?: unknown;
    regexp?: string | null;
    digitTimeOut?: number | null;
    timeOut?: number | null;
    silencePre?: number | null;
    silencePost?: number | null;
};

export type IvrApiRoute = {
    id?: number;
    flowId?: number;
    fromElementId: number;
    routeType: string;
    routeValue: string | null;
    toElementId: number;
    priority?: number;
    isActive?: boolean;
    tries?: number | null;
};

export type IvrApiSchedule = {
    id?: number;
    flowId?: number;
    elementId: number;
    priority: number;
    daysMask: number;
    fromTime: string;
    toTime: string;
    activeToId: number;
    isActive?: boolean;
};

export function ivrRawSchedulePriority(s: unknown): number {
    if (s == null || typeof s !== "object") return NaN;
    const r = s as Record<string, unknown>;
    const v = r.priority ?? r.Priority;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : NaN;
}

export function ivrRawScheduleElementId(s: unknown): number {
    if (s == null || typeof s !== "object") return NaN;
    const r = s as Record<string, unknown>;
    const v = r.elementId ?? r.ElementId;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : NaN;
}

export function ivrRawScheduleRowId(s: unknown): number {
    if (s == null || typeof s !== "object") return -1;
    const r = s as Record<string, unknown>;
    const v =
        r.id ??
        r.Id ??
        r.scheduleId ??
        r.ScheduleId ??
        r.scheduleID ??
        r.ScheduleID;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? n : -1;
}

export function ivrRawScheduleIsActive(s: unknown): boolean {
    if (s == null || typeof s !== "object") return true;
    const r = s as Record<string, unknown>;
    const v = r.isActive ?? r.IsActive;
    return v !== false;
}

export function resolveIvrSchedulesServerTruth(ivrConfig: unknown): IvrApiSchedule[] {
    const r = (ivrConfig ?? undefined) as Record<string, unknown> | undefined;
    if (r == null) {
        return [];
    }
    const snap = Array.isArray(r._ivrSchedulesServerSnapshot)
        ? (r._ivrSchedulesServerSnapshot as unknown[])
        : [];
    const live = Array.isArray(r.schedules)
        ? (r.schedules as unknown[])
        : Array.isArray(r.Schedules)
          ? (r.Schedules as unknown[])
          : [];

    const byId = new Map<number, IvrApiSchedule>();
    for (const row of snap) {
        if (row == null || typeof row !== "object") continue;
        const s = row as IvrApiSchedule;
        const id = ivrRawScheduleRowId(s);
        if (id > 0) {
            byId.set(id, s);
        }
    }
    for (const row of live) {
        if (row == null || typeof row !== "object") continue;
        const s = row as IvrApiSchedule;
        const id = ivrRawScheduleRowId(s);
        if (id > 0 && !byId.has(id)) {
            byId.set(id, s);
        }
    }
    if (byId.size > 0) {
        return Array.from(byId.values());
    }
    if (live.length > 0) {
        return [...(live as IvrApiSchedule[])];
    }
    return [...(snap as IvrApiSchedule[])];
}

export function ivrRawScheduleActiveToId(s: unknown): number {
    if (s == null || typeof s !== "object") return 0;
    const r = s as Record<string, unknown>;
    const v = r.activeToId ?? r.ActiveToId;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
}

function scheduleApiRecordMatchesSlotRow(
    s: unknown,
    row: IvrSchedulerSlotRow
): boolean {
    if (s == null || typeof s !== "object") return false;
    const r = s as Record<string, unknown>;
    const mask = Number(r.daysMask ?? r.DaysMask) || 0;
    if (mask !== row.daysMask) return false;
    const fs = trimTimeToHHMM(String(r.fromTime ?? r.FromTime ?? ""));
    const fe = trimTimeToHHMM(String(r.toTime ?? r.ToTime ?? ""));
    return (
        fs === trimTimeToHHMM(String(row.start)) &&
        fe === trimTimeToHHMM(String(row.end))
    );
}

export type IvrSchedulerJumpEdgeDeletionMode =
    | { kind: "slotRows"; rows: readonly IvrSchedulerSlotRow[] }
    | { kind: "allWithActiveTarget"; toElementId: number }
    | { kind: "none" };

export function schedulesToDeleteForSchedulerJumpEdge(args: {
    schedules: readonly IvrApiSchedule[];
    schedulerElementId: number;
    mode: IvrSchedulerJumpEdgeDeletionMode;
}): IvrApiSchedule[] {
    const { schedules, schedulerElementId, mode } = args;
    if (mode.kind === "none") return [];
    const persisted = schedules.filter((s) => {
        const id = ivrRawScheduleRowId(s);
        if (!(Number.isFinite(id) && id > 0)) return false;
        return ivrRawScheduleElementId(s) === schedulerElementId;
    });
    if (mode.kind === "allWithActiveTarget") {
        return persisted.filter(
            (s) => ivrRawScheduleActiveToId(s) === mode.toElementId
        );
    }
    const rows = mode.rows;
    if (!rows.length) return [];
    const toEl = rows[0]!.activeToId;
    const strict = persisted.filter(
        (s) =>
            ivrRawScheduleActiveToId(s) === toEl &&
            rows.some((row) => scheduleApiRecordMatchesSlotRow(s, row))
    );
    if (strict.length > 0) return strict;

    const allowMaskOnlyFallback = rows.every((row) => {
        const m = Number(row.daysMask) || 0;
        return m > 0 && (m & (m - 1)) === 0;
    });
    if (!allowMaskOnlyFallback) {
        return strict;
    }

    const byMask = persisted.filter((s) => {
        if (ivrRawScheduleActiveToId(s) !== toEl) return false;
        const sm =
            Number(
                (s as Record<string, unknown>).daysMask ??
                    (s as Record<string, unknown>).DaysMask
            ) || 0;
        if (!(sm > 0)) return false;
        return rows.some((row) => (Number(row.daysMask) || 0) === sm);
    });
    return byMask;
}

const DRAFT_SCHEDULER_DAY_LABELS: Record<number, string> = {
    1: "Понеділок",
    2: "Вівторок",
    3: "Середа",
    4: "Четвер",
    5: "П'ятниця",
    6: "Субота",
    0: "Неділя",
};

const DRAFT_DAY_SORT_ORDER: Record<number, number> = {
    1: 0,
    2: 1,
    3: 2,
    4: 3,
    5: 4,
    6: 5,
    0: 6,
};

export function mergeRemovedSchedulerApiSchedulesIntoDraftJson(
    schedulerEl: IvrApiElement,
    removed: readonly IvrApiSchedule[]
): string | undefined {
    if (!removed.length) return undefined;
    const prev = parseIvrElementDraftData(apiElementDraftDataRaw(schedulerEl));
    const dayMap = new Map<
        number,
        { dayOfWeek: number; label: string; enabled: boolean; start: string; end: string }
    >();
    for (const dow of [1, 2, 3, 4, 5, 6, 0] as const) {
        dayMap.set(dow, {
            dayOfWeek: dow,
            label: DRAFT_SCHEDULER_DAY_LABELS[dow] ?? "",
            enabled: false,
            start: "",
            end: "",
        });
    }
    if (prev && Array.isArray(prev.schedulerDays)) {
        for (const d of prev.schedulerDays as Record<string, unknown>[]) {
            const dow = Number(d.dayOfWeek);
            if (!Number.isFinite(dow)) continue;
            const cur = dayMap.get(dow);
            if (!cur) continue;
            dayMap.set(dow, {
                ...cur,
                enabled: Boolean(d.enabled),
                start: String(d.start ?? ""),
                end: String(d.end ?? ""),
            });
        }
    }
    for (const s of removed) {
        const mask = Number(
            (s as Record<string, unknown>).daysMask ??
                (s as Record<string, unknown>).DaysMask
        );
        if (!Number.isFinite(mask) || mask <= 0) continue;
        const fs = trimTimeToHHMM(
            String(
                (s as Record<string, unknown>).fromTime ??
                    (s as Record<string, unknown>).FromTime ??
                    ""
            )
        );
        const fe = trimTimeToHHMM(
            String(
                (s as Record<string, unknown>).toTime ??
                    (s as Record<string, unknown>).ToTime ??
                    ""
            )
        );
        for (const dow of dayOfWeeksFromDaysMask(mask)) {
            const cur = dayMap.get(dow);
            if (!cur) continue;
            dayMap.set(dow, {
                ...cur,
                enabled: true,
                start: fs,
                end: fe,
            });
        }
    }
    const schedulerDays = Array.from(dayMap.values()).sort(
        (a, b) =>
            (DRAFT_DAY_SORT_ORDER[a.dayOfWeek] ?? 999) -
            (DRAFT_DAY_SORT_ORDER[b.dayOfWeek] ?? 999)
    );
    const next: Record<string, unknown> = prev
        ? { ...prev }
        : { schemaVersion: 1 };
    next.schemaVersion = 1;
    if (schedulerEl.flowId != null && Number(schedulerEl.flowId) > 0) {
        next.flowId = schedulerEl.flowId;
    }
    if (Number.isFinite(schedulerEl.elementId) && schedulerEl.elementId > 0) {
        next.schedulerElementId = schedulerEl.elementId;
    }
    if (typeof next.schedulerScheduleKind !== "string") {
        next.schedulerScheduleKind = "daysweek";
    }
    next.schedulerDays = schedulerDays;

    const activeTargets = removed
        .map((s) => ivrRawScheduleActiveToId(s))
        .filter((id) => Number.isFinite(id) && id !== 0);
    const uniqActive = Array.from(new Set(activeTargets));
    if (uniqActive.length === 1) {
        next.schedulerActiveToId = uniqActive[0];
    }

    try {
        return JSON.stringify(next);
    } catch {
        return undefined;
    }
}

export type IvrSchedulerSlotRow = {
    id?: number;
    priority: number;
    daysMask: number;
    start: string;
    end: string;
    activeToId: number;
};

const DAYS_MASK_ORDER: { mask: number; dayOfWeek: number; short: string }[] = [
    { mask: 1, dayOfWeek: 1, short: "Пн" },
    { mask: 2, dayOfWeek: 2, short: "Вт" },
    { mask: 4, dayOfWeek: 3, short: "Ср" },
    { mask: 8, dayOfWeek: 4, short: "Чт" },
    { mask: 16, dayOfWeek: 5, short: "Пт" },
    { mask: 32, dayOfWeek: 6, short: "Сб" },
    { mask: 64, dayOfWeek: 0, short: "Нд" },
];

const SCHEDULER_ALL_DAYS_MASK = 127;

export function trimTimeToHHMM(t: string): string {
    const p = (t ?? "").trim().split(":");
    const h = String(parseInt(p[0] || "0", 10) || 0).padStart(2, "0");
    const m = String(parseInt(p[1] || "0", 10) || 0).padStart(2, "0");
    return `${h.slice(-2)}:${m.slice(0, 2)}`;
}

function dayOfWeeksFromDaysMask(mask: number): number[] {
    return DAYS_MASK_ORDER.filter((d) => (mask & d.mask) !== 0).map(
        (d) => d.dayOfWeek
    );
}

export function daysMaskToCompactLabel(mask: number): string {
    const idxs: number[] = [];
    DAYS_MASK_ORDER.forEach((d, i) => {
        if (mask & d.mask) idxs.push(i);
    });
    if (idxs.length === 0) return "—";
    if (idxs.length === 7) return "Усі дні";
    const runs: string[] = [];
    let rs = 0;
    for (let j = 1; j <= idxs.length; j++) {
        if (j === idxs.length || idxs[j] !== idxs[j - 1] + 1) {
            const a = idxs[rs];
            const b = idxs[j - 1];
            if (a === b) runs.push(DAYS_MASK_ORDER[a].short);
            else
                runs.push(
                    `${DAYS_MASK_ORDER[a].short}–${DAYS_MASK_ORDER[b].short}`
                );
            rs = j;
        }
    }
    return runs.join(", ");
}

export function formatSchedulerSlotNodeLabel(
    daysMask: number,
    start: string,
    end: string
): string {
    const time = `${start} - ${end}`;
    const mask = Number(daysMask) || 0;
    if (mask === 127) return time;
    const dayLabel = daysMaskToCompactLabel(mask);
    return dayLabel ? `${dayLabel}\n${time}` : time;
}

export function schedulerSlotRowsUnionMask(rows: IvrSchedulerSlotRow[]): number {
    return rows.reduce((acc, r) => acc | (Number(r.daysMask) || 0), 0);
}

function schedulerSlotRowStartEndMinutes(row: IvrSchedulerSlotRow): {
    start: number;
    end: number;
} | null {
    const sRaw = trimTimeToHHMM(String(row.start ?? ""));
    const eRaw = trimTimeToHHMM(String(row.end ?? ""));
    const ps = sRaw.split(":");
    const pe = eRaw.split(":");
    const sh = parseInt(ps[0] || "0", 10);
    const sm = parseInt(ps[1] || "0", 10);
    const eh = parseInt(pe[0] || "0", 10);
    const em = parseInt(pe[1] || "0", 10);
    if (
        !Number.isFinite(sh) ||
        !Number.isFinite(sm) ||
        !Number.isFinite(eh) ||
        !Number.isFinite(em)
    ) {
        return null;
    }
    return { start: sh * 60 + sm, end: eh * 60 + em };
}

function applySchedulerSlotToMinuteCoverage(
    covered: boolean[],
    startMin: number,
    endMin: number
): void {
    const fullStart = startMin === 0;
    const fullEnd =
        endMin === 23 * 60 + 59 ||
        endMin === 0 ||
        endMin >= 24 * 60;
    if (fullStart && fullEnd) {
        covered.fill(true);
        return;
    }
    if (startMin === endMin) return;

    const s = Math.max(0, Math.min(1439, startMin));
    const e = Math.max(0, Math.min(1439, endMin));
    if (s < e) {
        for (let m = s; m <= e; m++) covered[m] = true;
    } else if (s > e) {
        for (let m = s; m <= 1439; m++) covered[m] = true;
        for (let m = 0; m <= e; m++) covered[m] = true;
    }
}

function schedulerSlotRowsCoverFullDayForMaskBit(
    rows: IvrSchedulerSlotRow[],
    dayMaskBit: number
): boolean {
    const rowsForDay = rows.filter(
        (r) => (Number(r.daysMask) || 0) & dayMaskBit
    );
    if (!rowsForDay.length) return false;
    const covered = new Array(1440).fill(false);
    for (const r of rowsForDay) {
        const pair = schedulerSlotRowStartEndMinutes(r);
        if (!pair) return false;
        applySchedulerSlotToMinuteCoverage(covered, pair.start, pair.end);
    }
    return covered.every(Boolean);
}

export function schedulerSlotRowsNeedOthers(rows: IvrSchedulerSlotRow[]): boolean {
    if (!rows.length) return false;
    if (schedulerSlotRowsUnionMask(rows) !== 127) return true;
    return DAYS_MASK_ORDER.some(
        (d) => !schedulerSlotRowsCoverFullDayForMaskBit(rows, d.mask)
    );
}

function schedulerSlotJumpTargets(rows: IvrSchedulerSlotRow[]): Set<number> {
    return new Set(
        rows
            .map((r) => r.activeToId)
            .filter((id) => Number.isFinite(id) && !Number.isNaN(id))
    );
}

export function schedulerChainNeedsOthers(
    rows: IvrSchedulerSlotRow[],
    _elementId: number,
    _routes: IvrApiRoute[]
): boolean {
    if (!rows.length) return false;
    return schedulerSlotRowsNeedOthers(rows);
}

export function resolveIvrSchedulerPresentationKind(
    config: Record<string, unknown> | null | undefined
): "daily" | "weekdays" {
    if (!config) return "weekdays";
    const raw = String(config.schedulerScheduleKind ?? "")
        .trim()
        .toLowerCase();
    if (raw === "once_advanced" || raw === "once-advanced") return "weekdays";
    if (raw === "daysweek_advanced" || raw === "daysweek-advanced") {
        return "weekdays";
    }
    if (raw === "once" || raw === "daily") return "daily";
    const w = config.schedulerExtraWindow as
        | {
              start?: string;
              end?: string;
              start2?: string;
              end2?: string;
          }
        | undefined;
    if (
        w &&
        String(w.start ?? "").trim() &&
        String(w.end ?? "").trim() &&
        String(w.start2 ?? "").trim() &&
        String(w.end2 ?? "").trim()
    ) {
        return "daily";
    }
    if (raw === "daysweek" || raw === "weekdays") return "weekdays";
    const days =
        (config.schedulerDays as
            | { enabled?: boolean; start?: string; end?: string }[]
            | undefined) ?? [];
    const anyEnabled = days.some(
        (d) =>
            !!d?.enabled &&
            !!(d.start ?? "").trim() &&
            !!(d.end ?? "").trim()
    );
    const hasExtra = !!(w?.start && w?.end);
    return hasExtra && !anyEnabled ? "daily" : "weekdays";
}

function expandSlotRowsToSchedulerDays(
    rows: IvrSchedulerSlotRow[]
): {
    dayOfWeek: number;
    label: string;
    enabled: boolean;
    start: string;
    end: string;
}[] {
    const template: { dayOfWeek: number; label: string }[] = [
        { dayOfWeek: 1, label: "Понеділок" },
        { dayOfWeek: 2, label: "Вівторок" },
        { dayOfWeek: 3, label: "Середа" },
        { dayOfWeek: 4, label: "Четвер" },
        { dayOfWeek: 5, label: "П'ятниця" },
        { dayOfWeek: 6, label: "Субота" },
        { dayOfWeek: 0, label: "Неділя" },
    ];
    const merged = new Map<number, { start: string; end: string }>();
    const sorted = [...rows].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );
    for (const row of sorted) {
        for (const dow of dayOfWeeksFromDaysMask(row.daysMask)) {
            if (!merged.has(dow)) {
                merged.set(dow, { start: row.start, end: row.end });
            }
        }
    }
    return template.map((t) => {
        const m = merged.get(t.dayOfWeek);
        return {
            dayOfWeek: t.dayOfWeek,
            label: t.label,
            enabled: !!m,
            start: m?.start ?? "",
            end: m?.end ?? "",
        };
    });
}

function mergeSchedulerWeekdaysFromApiRowsAndDraft(
    apiSlotRows: IvrSchedulerSlotRow[],
    draftDays:
        | {
              dayOfWeek: number;
              enabled?: boolean;
              start?: string;
              end?: string;
          }[]
        | undefined,
    preferDraftWeekdays = false
): ReturnType<typeof expandSlotRowsToSchedulerDays> {
    const fromApi = expandSlotRowsToSchedulerDays(apiSlotRows);
    const draftExplicit = !isSchedulerDraftWeekdayEmptyShell(
        draftDays as
            | {
                  dayOfWeek: number;
                  enabled?: boolean;
                  start?: string;
                  end?: string;
              }[]
            | undefined
    );
    const draftByDow = new Map<
        number,
        {
            dayOfWeek: number;
            enabled?: boolean;
            start?: string;
            end?: string;
        }
    >();
    for (const d of draftDays ?? []) {
        draftByDow.set(Number(d.dayOfWeek), d);
    }
    return fromApi.map((slot) => {
        const dr = draftByDow.get(slot.dayOfWeek);
        if (draftExplicit && dr && dr.enabled === false) {
            return {
                dayOfWeek: slot.dayOfWeek,
                label: slot.label,
                enabled: false,
                start: "",
                end: "",
            };
        }
        if (preferDraftWeekdays && draftExplicit && dr) {
            const start = String(dr.start ?? "").trim();
            const end = String(dr.end ?? "").trim();
            if (dr.enabled && start && end) {
                return {
                    dayOfWeek: slot.dayOfWeek,
                    label: slot.label,
                    enabled: true,
                    start,
                    end,
                };
            }
            return {
                dayOfWeek: slot.dayOfWeek,
                label: slot.label,
                enabled: false,
                start: "",
                end: "",
            };
        }
        if (slot.enabled) return slot;
        if (
            dr &&
            dr.enabled &&
            String(dr.start ?? "").trim() &&
            String(dr.end ?? "").trim()
        ) {
            return {
                dayOfWeek: slot.dayOfWeek,
                label: slot.label,
                enabled: true,
                start: String(dr.start ?? ""),
                end: String(dr.end ?? ""),
            };
        }
        return slot;
    });
}

function isSingleWeekdayDaysMask(mask: number): boolean {
    const m = Number(mask) || 0;
    return m > 0 && (m & (m - 1)) === 0;
}

function filterApiSlotRowsForSchedulerKind(
    rows: IvrSchedulerSlotRow[],
    kind: ReturnType<typeof normalizeIvrSchedulerScheduleKind>
): IvrSchedulerSlotRow[] {
    if (kind !== "daysweek") return rows;
    return rows.filter((r) => {
        const m = Number(r.daysMask) || 0;
        return m !== SCHEDULER_ALL_DAYS_MASK && isSingleWeekdayDaysMask(m);
    });
}
export function ivrSchedulerSlotNodeIdSuffix(
    sortedRows: IvrSchedulerSlotRow[],
    rowIndex: number
): string {
    const row = sortedRows[rowIndex];
    if (!row) return `x-${rowIndex}`;
    const mask = Number(row.daysMask) || 0;
    const idRaw = row.id;
    const idNum =
        typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
    if (Number.isFinite(idNum) && idNum > 0) {
        return `${mask}-id-${idNum}`;
    }
    const sameMaskCount = sortedRows.filter(
        (r) => (Number(r.daysMask) || 0) === mask
    ).length;
    if (mask === SCHEDULER_ALL_DAYS_MASK && sameMaskCount > 1) {
        const s = trimTimeToHHMM(String(row.start ?? "")).replace(/:/g, "");
        const e = trimTimeToHHMM(String(row.end ?? "")).replace(/:/g, "");
        return `${mask}-t${s}-${e}`;
    }
    if (sameMaskCount <= 1) return String(mask);
    let occ = 0;
    for (let i = 0; i < rowIndex; i++) {
        if ((Number(sortedRows[i]?.daysMask) || 0) === mask) occ++;
    }
    return `${mask}-${occ}`;
}

export function ivrSchedulerSlotRowIndexFromSlotSuffix(
    suffix: string,
    sortedRows: IvrSchedulerSlotRow[]
): number {
    for (let i = 0; i < sortedRows.length; i++) {
        if (ivrSchedulerSlotNodeIdSuffix(sortedRows, i) === suffix) return i;
    }
    const byPersistedId = /^(\d+)-id-(\d+)$/.exec(suffix);
    if (byPersistedId) {
        const wantId = parseInt(byPersistedId[2]!, 10);
        if (Number.isFinite(wantId) && wantId > 0) {
            const ix = sortedRows.findIndex((r) => {
                const idRaw = r.id;
                const idNum =
                    typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
                return Number.isFinite(idNum) && idNum === wantId;
            });
            if (ix >= 0) return ix;
        }
    }
    const byTime = /^127-t(\d{4})-(\d{4})$/.exec(suffix);
    if (byTime) {
        const start = `${byTime[1]!.slice(0, 2)}:${byTime[1]!.slice(2)}`;
        const end = `${byTime[2]!.slice(0, 2)}:${byTime[2]!.slice(2)}`;
        const ix = sortedRows.findIndex(
            (r) =>
                (Number(r.daysMask) || 0) === SCHEDULER_ALL_DAYS_MASK &&
                trimTimeToHHMM(String(r.start ?? "")) === start &&
                trimTimeToHHMM(String(r.end ?? "")) === end
        );
        if (ix >= 0) return ix;
    }
    const legacy = /^(\d+)-(\d+)$/.exec(suffix);
    if (legacy) {
        const si = parseInt(legacy[2]!, 10);
        if (
            Number.isFinite(si) &&
            si >= 0 &&
            si < sortedRows.length &&
            ivrSchedulerSlotNodeIdSuffix(sortedRows, si) === suffix
        ) {
            return si;
        }
    }
    return -1;
}

function daysMaskFirstSortIndex(mask: number): number {
    const m = Number(mask) || 0;
    for (let i = 0; i < DAYS_MASK_ORDER.length; i++) {
        if ((m & DAYS_MASK_ORDER[i].mask) !== 0) return i;
    }
    return 999;
}

function renumberSchedulerSlotPriorities(
    rows: IvrSchedulerSlotRow[]
): IvrSchedulerSlotRow[] {
    return rows.map((r, i) => ({
        ...r,
        priority: i + 1,
    }));
}

function schedulerSlotRowTimeMaskKey(row: IvrSchedulerSlotRow): string {
    const mask = Number(row.daysMask) || 0;
    const s = trimTimeToHHMM(String(row.start ?? ""));
    const e = trimTimeToHHMM(String(row.end ?? ""));
    return `${mask}|${s}|${e}`;
}

function mergeDailyApiSlotRowsWithDraft(
    apiSlotRows: IvrSchedulerSlotRow[],
    mergedConfig: Record<string, unknown>
): IvrSchedulerSlotRow[] {
    const rawAid = mergedConfig.schedulerActiveToId;
    const aid =
        typeof rawAid === "number" ? rawAid : Number(rawAid);
    let activeToId = Number.isFinite(aid) && aid >= 0 ? aid : 0;
    if (activeToId === 0 && apiSlotRows.length > 0) {
        const z = apiSlotRows[0]?.activeToId;
        activeToId = typeof z === "number" ? z : Number(z);
        if (!Number.isFinite(activeToId) || activeToId < 0) activeToId = 0;
    }

    const { schedulerSlotRows: _omitRows, ...mergedForOnceDraft } = mergedConfig;
    const fromDraft = buildSchedulerSlotRowsFromBlockConfig(
        mergedForOnceDraft,
        activeToId
    );

    if (apiSlotRows.length === 0) {
        return fromDraft;
    }

    if (fromDraft.length === 0) {
        return renumberSchedulerSlotPriorities([...apiSlotRows]);
    }

    const buckets = new Map<string, IvrSchedulerSlotRow[]>();
    for (const r of apiSlotRows) {
        const k = schedulerSlotRowTimeMaskKey(r);
        const arr = buckets.get(k);
        if (arr) arr.push(r);
        else buckets.set(k, [r]);
    }

    const takeApiMatch = (draftRow: IvrSchedulerSlotRow): IvrSchedulerSlotRow => {
        const k = schedulerSlotRowTimeMaskKey(draftRow);
        const arr = buckets.get(k);
        if (arr && arr.length > 0) {
            const hit = arr.shift()!;
            if (arr.length === 0) buckets.delete(k);
            return hit;
        }
        return {
            ...draftRow,
            id: undefined,
        };
    };

    const mergedList = fromDraft.map(takeApiMatch);
    const leftoverApi: IvrSchedulerSlotRow[] = [];
    for (const arr of Array.from(buckets.values())) {
        leftoverApi.push(...arr);
    }
    leftoverApi.sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );

    return renumberSchedulerSlotPriorities([...mergedList, ...leftoverApi]);
}

function mergeDaysweekAdvancedApiSlotRowsWithDraft(
    apiSlotRows: IvrSchedulerSlotRow[],
    mergedConfig: Record<string, unknown>
): IvrSchedulerSlotRow[] {
    const isSingleWeekdayMask = (mask: number) => {
        const m = Number(mask) || 0;
        return m > 0 && (m & (m - 1)) === 0;
    };
    const apiRows = apiSlotRows.filter((r) =>
        isSingleWeekdayMask(Number(r.daysMask) || 0)
    );
    const draftAll =
        (mergedConfig.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
        [];
    const draftRows = draftAll.filter((r) =>
        isSingleWeekdayMask(Number(r.daysMask) || 0)
    );

    const rawAid = mergedConfig.schedulerActiveToId;
    const aid =
        typeof rawAid === "number" ? rawAid : Number(rawAid);
    let activeToId = Number.isFinite(aid) && aid >= 0 ? aid : 0;
    if (activeToId === 0 && apiRows.length > 0) {
        const z = apiRows[0]?.activeToId;
        activeToId = typeof z === "number" ? z : Number(z);
        if (!Number.isFinite(activeToId) || activeToId < 0) activeToId = 0;
    }

    const fromDraftSorted = [...draftRows].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );

    if (apiRows.length === 0) {
        return renumberSchedulerSlotPriorities(
            fromDraftSorted.map((r) => ({
                ...r,
                activeToId:
                    Number(r.activeToId) && Number(r.activeToId) > 0
                        ? Number(r.activeToId)
                        : activeToId,
            }))
        );
    }

    const buckets = new Map<string, IvrSchedulerSlotRow[]>();
    for (const r of apiRows) {
        const k = schedulerSlotRowTimeMaskKey(r);
        const arr = buckets.get(k);
        if (arr) arr.push(r);
        else buckets.set(k, [r]);
    }

    const mergedList: IvrSchedulerSlotRow[] = fromDraftSorted.map(
        (draftRow) => {
            const k = schedulerSlotRowTimeMaskKey(draftRow);
            const arr = buckets.get(k);
            if (arr && arr.length > 0) {
                const hit = arr.shift()!;
                const hid =
                    typeof hit.id === "number"
                        ? hit.id
                        : Number(hit.id ?? 0);
                return {
                    ...draftRow,
                    id: Number.isFinite(hid) && hid > 0 ? hid : draftRow.id,
                    priority: draftRow.priority ?? hit.priority,
                    activeToId:
                        Number(hit.activeToId) > 0
                            ? Number(hit.activeToId)
                            : Number(draftRow.activeToId) > 0
                              ? Number(draftRow.activeToId)
                              : activeToId,
                };
            }
            return {
                ...draftRow,
                activeToId:
                    Number(draftRow.activeToId) > 0
                        ? Number(draftRow.activeToId)
                        : activeToId,
            };
        }
    );

    const leftoverApi: IvrSchedulerSlotRow[] = [];
    for (const arr of Array.from(buckets.values())) {
        leftoverApi.push(...arr);
    }
    leftoverApi.sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );

    if (fromDraftSorted.length > 0) {
        return renumberSchedulerSlotPriorities(mergedList);
    }
    return renumberSchedulerSlotPriorities([...mergedList, ...leftoverApi]);
}

function mergeOnceAdvancedApiSlotRowsWithDraft(
    apiSlotRows: IvrSchedulerSlotRow[],
    mergedConfig: Record<string, unknown>
): IvrSchedulerSlotRow[] {
    const maskAll = 127;
    const api127 = apiSlotRows.filter(
        (r) => (Number(r.daysMask) || 0) === maskAll
    );
    const draftAll =
        (mergedConfig.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
        [];
    const draft127 = draftAll.filter(
        (r) => (Number(r.daysMask) || 0) === maskAll
    );

    const rawAid = mergedConfig.schedulerActiveToId;
    const aid =
        typeof rawAid === "number" ? rawAid : Number(rawAid);
    let activeToId = Number.isFinite(aid) && aid >= 0 ? aid : 0;
    if (activeToId === 0 && api127.length > 0) {
        const z = api127[0]?.activeToId;
        activeToId = typeof z === "number" ? z : Number(z);
        if (!Number.isFinite(activeToId) || activeToId < 0) activeToId = 0;
    }

    const fromDraftSorted = [...draft127].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );

    if (api127.length === 0) {
        return renumberSchedulerSlotPriorities(
            fromDraftSorted.map((r) => ({
                ...r,
                activeToId:
                    Number(r.activeToId) && Number(r.activeToId) > 0
                        ? Number(r.activeToId)
                        : activeToId,
            }))
        );
    }

    const buckets = new Map<string, IvrSchedulerSlotRow[]>();
    for (const r of api127) {
        const k = schedulerSlotRowTimeMaskKey(r);
        const arr = buckets.get(k);
        if (arr) arr.push(r);
        else buckets.set(k, [r]);
    }

    const mergedList: IvrSchedulerSlotRow[] = fromDraftSorted.map(
        (draftRow) => {
            const k = schedulerSlotRowTimeMaskKey(draftRow);
            const arr = buckets.get(k);
            if (arr && arr.length > 0) {
                const hit = arr.shift()!;
                const hid =
                    typeof hit.id === "number"
                        ? hit.id
                        : Number(hit.id ?? 0);
                return {
                    ...draftRow,
                    id: Number.isFinite(hid) && hid > 0 ? hid : draftRow.id,
                    priority: draftRow.priority ?? hit.priority,
                    activeToId:
                        Number(hit.activeToId) > 0
                            ? Number(hit.activeToId)
                            : Number(draftRow.activeToId) > 0
                              ? Number(draftRow.activeToId)
                              : activeToId,
                };
            }
            return {
                ...draftRow,
                activeToId:
                    Number(draftRow.activeToId) > 0
                        ? Number(draftRow.activeToId)
                        : activeToId,
            };
        }
    );

    const leftoverApi: IvrSchedulerSlotRow[] = [];
    for (const arr of Array.from(buckets.values())) {
        leftoverApi.push(...arr);
    }
    leftoverApi.sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );

    if (fromDraftSorted.length > 0) {
        return renumberSchedulerSlotPriorities(mergedList);
    }
    return renumberSchedulerSlotPriorities([...mergedList, ...leftoverApi]);
}

function attachPersistedIdsToBuiltSchedulerRows(
    built: IvrSchedulerSlotRow[],
    apiSlotRows: IvrSchedulerSlotRow[]
): IvrSchedulerSlotRow[] {
    const buckets = new Map<string, IvrSchedulerSlotRow[]>();
    for (const r of apiSlotRows) {
        const m = Number(r.daysMask) || 0;
        if (m === SCHEDULER_ALL_DAYS_MASK) {
            continue;
        }
        if (!(m > 0 && (m & (m - 1)) === 0)) {
            continue;
        }
        const k = schedulerSlotRowTimeMaskKey(r);
        const arr = buckets.get(k);
        if (arr) arr.push(r);
        else buckets.set(k, [r]);
    }
    return built.map((row) => {
        const k = schedulerSlotRowTimeMaskKey(row);
        const arr = buckets.get(k);
        if (arr && arr.length > 0) {
            const hit = arr.shift()!;
            const hid =
                typeof hit.id === "number" ? hit.id : Number(hit.id ?? 0);
            return {
                ...row,
                id: Number.isFinite(hid) && hid > 0 ? hid : row.id,
                activeToId:
                    Number(hit.activeToId) > 0
                        ? Number(hit.activeToId)
                        : row.activeToId,
            };
        }
        return row;
    });
}

function mergeApiSchedulerSlotRowsWithDraftWeekdays(
    apiSlotRows: IvrSchedulerSlotRow[],
    mergedConfig: Record<string, unknown>
): IvrSchedulerSlotRow[] {
    const storedKind = normalizeIvrSchedulerScheduleKind(
        mergedConfig.schedulerScheduleKind
    );
    if (storedKind === "once_advanced") {
        return mergeOnceAdvancedApiSlotRowsWithDraft(apiSlotRows, mergedConfig);
    }
    if (storedKind === "daysweek_advanced") {
        return mergeDaysweekAdvancedApiSlotRowsWithDraft(
            apiSlotRows,
            mergedConfig
        );
    }
    if (storedKind === "daysweek") {
        const rawAid = mergedConfig.schedulerActiveToId;
        const aid =
            typeof rawAid === "number" ? rawAid : Number(rawAid);
        const activeToId =
            Number.isFinite(aid) && aid >= 0 ? aid : 0;
        const built = buildSchedulerSlotRowsFromBlockConfig(
            mergedConfig,
            activeToId
        );
        return attachPersistedIdsToBuiltSchedulerRows(built, apiSlotRows);
    }
    if (resolveIvrSchedulerPresentationKind(mergedConfig) === "daily") {
        return mergeDailyApiSlotRowsWithDraft(apiSlotRows, mergedConfig);
    }

    const apiMask = schedulerSlotRowsUnionMask(apiSlotRows);
    const rawAid = mergedConfig.schedulerActiveToId;
    const aid =
        typeof rawAid === "number" ? rawAid : Number(rawAid);
    const defaultActive =
        Number.isFinite(aid) && aid >= 0 ? aid : 0;

    const days =
        (mergedConfig.schedulerDays as {
            dayOfWeek: number;
            enabled: boolean;
            start: string;
            end: string;
        }[]) ?? [];
    const daySortOrder: Record<number, number> = {
        1: 0,
        2: 1,
        3: 2,
        4: 3,
        5: 4,
        6: 5,
        0: 6,
    };
    const extras: IvrSchedulerSlotRow[] = [];
    for (const d of [...days].sort(
        (a, b) =>
            (daySortOrder[a.dayOfWeek] ?? 999) -
            (daySortOrder[b.dayOfWeek] ?? 999)
    )) {
        if (
            !d.enabled ||
            !String(d.start ?? "").trim() ||
            !String(d.end ?? "").trim()
        ) {
            continue;
        }
        const m = schedulerDayOfWeekToDaysMask(d.dayOfWeek);
        if (!m) continue;
        if ((apiMask & m) !== 0) continue;
        extras.push({
            priority: 0,
            daysMask: m,
            start: trimTimeToHHMM(d.start),
            end: trimTimeToHHMM(d.end),
            activeToId: defaultActive,
        });
    }

    const combined = [...apiSlotRows, ...extras];
    combined.sort((a, b) => {
        const ia = daysMaskFirstSortIndex(a.daysMask);
        const ib = daysMaskFirstSortIndex(b.daysMask);
        if (ia !== ib) return ia - ib;
        return (a.priority ?? 0) - (b.priority ?? 0);
    });
    return renumberSchedulerSlotPriorities(combined);
}

function schedulesForElement(
    elementId: number,
    schedules: IvrApiSchedule[] | undefined
): IvrApiSchedule[] {
    if (!schedules?.length) return [];
    return [...schedules]
        .filter((s) => ivrRawScheduleElementId(s) === elementId)
        .filter((s) => ivrRawScheduleIsActive(s))
        .sort(
            (a, b) =>
                (ivrRawSchedulePriority(a) || 0) - (ivrRawSchedulePriority(b) || 0)
        );
}

function apiSchedulesToSlotRows(list: IvrApiSchedule[]): IvrSchedulerSlotRow[] {
    return list.map((s) => {
        const r = s as Record<string, unknown>;
        return {
            id: (r.id ?? r.Id) as number | undefined,
            priority: ivrRawSchedulePriority(s) || 0,
            daysMask: Number(r.daysMask ?? r.DaysMask) || 0,
            start: trimTimeToHHMM(String(r.fromTime ?? r.FromTime ?? "")),
            end: trimTimeToHHMM(String(r.toTime ?? r.ToTime ?? "")),
            activeToId: Number(r.activeToId ?? r.ActiveToId) || 0,
        };
    });
}

export function schedulerSlotRowIsPersistedInApi(
    schedules: IvrApiSchedule[] | undefined,
    elementId: number,
    row: IvrSchedulerSlotRow
): boolean {
    if (row.id != null) {
        const idn = typeof row.id === "number" ? row.id : Number(row.id);
        if (Number.isFinite(idn) && idn > 0) return true;
    }
    if (!schedules?.length) return false;
    const mask = Number(row.daysMask) || 0;
    const rs = trimTimeToHHMM(String(row.start ?? ""));
    const re = trimTimeToHHMM(String(row.end ?? ""));
    for (const s of schedules) {
        if (ivrRawScheduleElementId(s) !== elementId) continue;
        const sr = s as Record<string, unknown>;
        if ((Number(sr.daysMask ?? sr.DaysMask) || 0) !== mask) continue;
        if (trimTimeToHHMM(String(sr.fromTime ?? sr.FromTime ?? "")) !== rs)
            continue;
        if (trimTimeToHHMM(String(sr.toTime ?? sr.ToTime ?? "")) !== re)
            continue;
        return true;
    }
    return false;
}

export function schedulerBlockConfigPatchFromSchedules(
    elementId: number,
    schedules: IvrApiSchedule[] | undefined,
    routes: IvrApiRoute[],
    scheduleKind?: ReturnType<typeof normalizeIvrSchedulerScheduleKind>
): {
    schedulerSlotRows: IvrSchedulerSlotRow[];
    schedulerDays: ReturnType<typeof expandSlotRowsToSchedulerDays>;
    schedulerActiveToId: number | null;
    schedulerShowOthers: boolean;
} {
    const apiRows = schedulesForElement(elementId, schedules);
    const allSlotRows = apiSchedulesToSlotRows(apiRows);
    const kind = scheduleKind ?? "daysweek";
    const filteredSlotRows = filterApiSlotRowsForSchedulerKind(
        allSlotRows,
        kind
    );
    const slotRowsForDays =
        kind === "daysweek"
            ? filteredSlotRows
            : filteredSlotRows.length > 0
              ? filteredSlotRows
              : allSlotRows;
    const schedulerDays = expandSlotRowsToSchedulerDays(slotRowsForDays);
    const slotRows =
        kind === "daysweek"
            ? filteredSlotRows
            : filteredSlotRows.length > 0
              ? filteredSlotRows
              : allSlotRows;
    return {
        schedulerSlotRows: slotRows,
        schedulerDays,
        schedulerActiveToId:
            slotRows.length > 0 ? slotRows[0]?.activeToId ?? null : null,
        schedulerShowOthers: schedulerChainNeedsOthers(
            slotRows,
            elementId,
            routes
        ),
    };
}

export function schedulerDayOfWeekToDaysMask(dayOfWeek: number): number {
    const f = DAYS_MASK_ORDER.find((d) => d.dayOfWeek === dayOfWeek);
    return f?.mask ?? 0;
}

export function ivrSchedulerWeekdayChainDayOfWeek(
    sourceNodeId: string
): number | null {
    const m = /-day-(\d+)$/.exec(sourceNodeId);
    if (!m) {
        return null;
    }
    const d = parseInt(m[1]!, 10);
    return Number.isFinite(d) ? d : null;
}

export type SchedulerOnceExtraWindow = {
    start?: string;
    end?: string;
    start2?: string;
    end2?: string;
};

/** Once: extraWindow з blockConfig або з двох слотів mask 127 (після завантаження з API). */
export function resolveSchedulerOnceExtraWindow(
    config: Record<string, unknown>
): SchedulerOnceExtraWindow | undefined {
    const w = config.schedulerExtraWindow as SchedulerOnceExtraWindow | undefined;
    if (w?.start?.trim() && w?.end?.trim()) {
        return w;
    }
    const rows = [
        ...((config.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
            []),
    ]
        .filter((r) => (Number(r.daysMask) || 0) === SCHEDULER_ALL_DAYS_MASK)
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    if (rows.length < 2) {
        return w;
    }
    return {
        start: trimTimeToHHMM(String(rows[0]!.start ?? "")),
        end: trimTimeToHHMM(String(rows[0]!.end ?? "")),
        start2: trimTimeToHHMM(String(rows[1]!.start ?? "")),
        end2: trimTimeToHHMM(String(rows[1]!.end ?? "")),
    };
}

export function schedulerOnceDailyIsFullDay(start: string, end: string): boolean {
    const s = trimTimeToHHMM(start);
    const e = trimTimeToHHMM(end);
    return s === "00:00" && (e === "23:59" || e === "00:00");
}

function schedulerOnceDailyRestLabel(
    start: string,
    end: string,
    start2?: string,
    end2?: string
): string {
    const s2 = (start2 ?? "").trim();
    const e2 = (end2 ?? "").trim();
    if (s2 && e2) {
        return `${trimTimeToHHMM(s2)} - ${trimTimeToHHMM(e2)}`;
    }
    const parse = (t: string) => {
        const p = trimTimeToHHMM(t).split(":");
        const h = parseInt(p[0] || "0", 10) || 0;
        const m = parseInt(p[1] || "0", 10) || 0;
        return { h, m };
    };
    const a = parse(start);
    const b = parse(end);
    const sh = String(b.h).padStart(2, "0");
    const sm = String(b.m).padStart(2, "0");
    const eh = String(a.h).padStart(2, "0");
    const em = String(a.m).padStart(2, "0");
    return `${sh}:${sm} - ${eh}:${em}`;
}

function buildOnceSlotRowsFromExtraWindow(
    w: SchedulerOnceExtraWindow,
    activeToId: number
): IvrSchedulerSlotRow[] {
    if (!w.start?.trim() || !w.end?.trim()) return [];
    const rows: IvrSchedulerSlotRow[] = [
        {
            priority: 1,
            daysMask: SCHEDULER_ALL_DAYS_MASK,
            start: trimTimeToHHMM(w.start),
            end: trimTimeToHHMM(w.end),
            activeToId,
        },
    ];
    if (w.start2?.trim() && w.end2?.trim()) {
        rows.push({
            priority: 2,
            daysMask: SCHEDULER_ALL_DAYS_MASK,
            start: trimTimeToHHMM(w.start2),
            end: trimTimeToHHMM(w.end2),
            activeToId,
        });
    }
    return rows;
}

/** Перетин інтервалів у межах доби (half-open [start,end) у хвилинах), лише для mask 127. */
export function ivrSchedulerDailySlotRowsOverlap(
    rows: IvrSchedulerSlotRow[]
): boolean {
    const dayRows = rows.filter(
        (r) => (Number(r.daysMask) || 0) === SCHEDULER_ALL_DAYS_MASK
    );
    const counts = new Array(1440).fill(0);
    for (const r of dayRows) {
        const p = schedulerSlotRowStartEndMinutes(r);
        if (!p || p.start === p.end) continue;
        const { start: s, end: e } = p;
        const bump = (m: number) => {
            const mm = ((m % 1440) + 1440) % 1440;
            counts[mm]++;
        };
        if (s < e) {
            for (let m = s; m < e; m++) bump(m);
        } else if (s > e) {
            for (let m = s; m < 1440; m++) bump(m);
            for (let m = 0; m < e; m++) bump(m);
        }
    }
    return counts.some((c) => c > 1);
}

function onceAdvancedIntervalHalfOpenRanges(
    startMin: number,
    endMin: number
): { start: number; end: number }[] {
    if (startMin === endMin) return [];
    if (startMin < endMin) return [{ start: startMin, end: endMin }];
    return [
        { start: startMin, end: 1440 },
        { start: 0, end: endMin },
    ];
}

function onceAdvancedHalfOpenRangesOverlap(
    s1: number,
    e1: number,
    s2: number,
    e2: number
): boolean {
    const a = onceAdvancedIntervalHalfOpenRanges(s1, e1);
    const b = onceAdvancedIntervalHalfOpenRanges(s2, e2);
    for (const ra of a) {
        for (const rb of b) {
            if (ra.start < rb.end && rb.start < ra.end) return true;
        }
    }
    return false;
}

function onceAdvancedUiRowToMinutes(
    start: Date | null | undefined,
    end: Date | null | undefined
): { start: number; end: number } | null {
    if (start == null || end == null) return null;
    const s = start.getHours() * 60 + start.getMinutes();
    const e = end.getHours() * 60 + end.getMinutes();
    return { start: s, end: e };
}

/** Індекси рядків Once (advanced) з помилками: порожні поля, нульовий інтервал, перетин, дубль. */
export function onceAdvancedSchedulerInvalidRowIndices(
    rows: readonly {
        start: Date | null;
        end: Date | null;
    }[]
): Set<number> {
    const invalid = new Set<number>();
    const parsed: { index: number; start: number; end: number }[] = [];
    const seen = new Map<string, number>();

    rows.forEach((row, index) => {
        const pair = onceAdvancedUiRowToMinutes(row.start, row.end);
        if (!pair) {
            invalid.add(index);
            return;
        }
        if (pair.start === pair.end) {
            invalid.add(index);
            return;
        }
        const key = `${pair.start}|${pair.end}`;
        const prevIdx = seen.get(key);
        if (prevIdx != null) {
            invalid.add(index);
            invalid.add(prevIdx);
        } else {
            seen.set(key, index);
        }
        parsed.push({ index, start: pair.start, end: pair.end });
    });

    for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
            const a = parsed[i]!;
            const b = parsed[j]!;
            if (
                onceAdvancedHalfOpenRangesOverlap(
                    a.start,
                    a.end,
                    b.start,
                    b.end
                )
            ) {
                invalid.add(a.index);
                invalid.add(b.index);
            }
        }
    }

    if (parsed.length === 0 && rows.length > 0) {
        rows.forEach((_, index) => invalid.add(index));
    }

    return invalid;
}

function onceAdvancedApplyIntervalToMinuteCoverage(
    covered: boolean[],
    startMin: number,
    endMin: number
): void {
    const fullStart = startMin === 0;
    const fullEnd =
        endMin === 23 * 60 + 59 ||
        endMin === 0 ||
        endMin >= 24 * 60;
    if (fullStart && fullEnd) {
        covered.fill(true);
        return;
    }
    if (startMin === endMin) return;

    const s = Math.max(0, Math.min(1439, startMin));
    const e = Math.max(0, Math.min(1439, endMin));
    if (s < e) {
        for (let m = s; m <= e; m++) covered[m] = true;
    } else if (s > e) {
        for (let m = s; m <= 1439; m++) covered[m] = true;
        for (let m = 0; m <= e; m++) covered[m] = true;
    }
}

function onceAdvancedMinuteRangesCoverFullDay(
    ranges: readonly { start: number; end: number }[]
): boolean {
    if (!ranges.length) return false;
    const covered = new Array(1440).fill(false);
    for (const { start, end } of ranges) {
        onceAdvancedApplyIntervalToMinuteCoverage(covered, start, end);
    }
    return covered.every(Boolean);
}

/** Once (advanced): чи залишились непокриті хвилини доби (потрібен Others). */
export function onceAdvancedSchedulerSlotRowsHaveCoverageGaps(
    rows: readonly IvrSchedulerSlotRow[]
): boolean {
    if (!rows.length) return true;
    const parsed: { start: number; end: number }[] = [];
    for (const r of rows) {
        const pair = schedulerSlotRowStartEndMinutes(r);
        if (!pair || pair.start === pair.end) continue;
        parsed.push(pair);
    }
    if (!parsed.length) return true;
    return !onceAdvancedMinuteRangesCoverFullDay(parsed);
}

/** Перевірка пропусків у добі для рядків UI Once (advanced) перед збереженням. */
export function onceAdvancedUiRowsHaveCoverageGaps(
    rows: readonly {
        start: Date | null;
        end: Date | null;
    }[]
): boolean {
    const parsed: { start: number; end: number }[] = [];
    for (const row of rows) {
        const pair = onceAdvancedUiRowToMinutes(row.start, row.end);
        if (!pair || pair.start === pair.end) continue;
        parsed.push(pair);
    }
    if (!parsed.length) return true;
    return !onceAdvancedMinuteRangesCoverFullDay(parsed);
}

export function daysweekAdvancedSchedulerInvalidKeys(
    days: readonly {
        dayOfWeek: number;
        enabled: boolean;
        intervals: readonly { start: Date | null; end: Date | null }[];
    }[]
): Set<string> {
    const invalid = new Set<string>();
    for (const day of days) {
        if (!day.enabled) {
            continue;
        }
        const intervals = day.intervals ?? [];
        if (intervals.length === 0) {
            invalid.add(`${day.dayOfWeek}-0`);
            continue;
        }
        const localInvalid = onceAdvancedSchedulerInvalidRowIndices(intervals);
        localInvalid.forEach((i) => invalid.add(`${day.dayOfWeek}-${i}`));
    }
    return invalid;
}

export function ivrSchedulerSlotRowsOverlapWithinSameDaysMask(
    rows: IvrSchedulerSlotRow[]
): boolean {
    const byMask = new Map<number, IvrSchedulerSlotRow[]>();
    for (const r of rows) {
        const m = Number(r.daysMask) || 0;
        if (m <= 0 || m === SCHEDULER_ALL_DAYS_MASK) {
            continue;
        }
        const arr = byMask.get(m) ?? [];
        arr.push(r);
        byMask.set(m, arr);
    }
    for (const group of Array.from(byMask.values())) {
        const asAllDays = group.map((r) => ({
            ...r,
            daysMask: SCHEDULER_ALL_DAYS_MASK,
        }));
        if (ivrSchedulerDailySlotRowsOverlap(asAllDays)) {
            return true;
        }
    }
    return false;
}

export function buildSchedulerSlotRowsFromBlockConfig(
    blockConfig: Record<string, unknown>,
    activeToId: number
): IvrSchedulerSlotRow[] {
    if (!Number.isFinite(activeToId) || activeToId < 0) return [];

    const renumber = (rows: IvrSchedulerSlotRow[]): IvrSchedulerSlotRow[] =>
        rows.map((r, i) => ({
            ...r,
            priority: i + 1,
            activeToId,
        }));

    const storedKind = normalizeIvrSchedulerScheduleKind(
        blockConfig.schedulerScheduleKind
    );

    if (storedKind === "daysweek_advanced") {
        const existing = blockConfig.schedulerSlotRows as
            | IvrSchedulerSlotRow[]
            | undefined;
        if (Array.isArray(existing) && existing.length > 0) {
            return [...existing]
                .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                .map((r, i) => ({
                    ...r,
                    priority: i + 1,
                    activeToId:
                        Number(r.activeToId) > 0 ? Number(r.activeToId) : 0,
                }));
        }
        return [];
    }

    if (storedKind === "once_advanced") {
        const existing = blockConfig.schedulerSlotRows as
            | IvrSchedulerSlotRow[]
            | undefined;
        if (Array.isArray(existing) && existing.length > 0) {
            return renumber(
                [...existing].sort(
                    (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
                )
            );
        }
        return [];
    }

    if (storedKind === "daysweek") {
        const days =
            (blockConfig.schedulerDays as {
                dayOfWeek: number;
                enabled: boolean;
                start: string;
                end: string;
            }[]) ?? [];
        const daySortOrder: Record<number, number> = {
            1: 0,
            2: 1,
            3: 2,
            4: 3,
            5: 4,
            6: 5,
            0: 6,
        };
        const enabled = days
            .filter(
                (d) =>
                    d.enabled &&
                    String(d.start ?? "").trim() &&
                    String(d.end ?? "").trim()
            )
            .sort(
                (a, b) =>
                    (daySortOrder[a.dayOfWeek] ?? 999) -
                    (daySortOrder[b.dayOfWeek] ?? 999)
            );
        return renumber(
            enabled.map((d) => ({
                priority: 0,
                daysMask: schedulerDayOfWeekToDaysMask(d.dayOfWeek),
                start: trimTimeToHHMM(d.start),
                end: trimTimeToHHMM(d.end),
                activeToId,
            }))
        );
    }

    if (storedKind === "once") {
        const w = resolveSchedulerOnceExtraWindow(blockConfig);
        if (w?.start?.trim() && w?.end?.trim()) {
            return renumber(buildOnceSlotRowsFromExtraWindow(w, activeToId));
        }
        const existingOnce = blockConfig.schedulerSlotRows as
            | IvrSchedulerSlotRow[]
            | undefined;
        if (Array.isArray(existingOnce) && existingOnce.length > 0) {
            const mask127 = existingOnce.filter(
                (r) => (Number(r.daysMask) || 0) === SCHEDULER_ALL_DAYS_MASK
            );
            if (mask127.length > 0) {
                return renumber(
                    [...mask127].sort(
                        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
                    )
                );
            }
        }
        return [];
    }

    const existing = blockConfig.schedulerSlotRows as
        | IvrSchedulerSlotRow[]
        | undefined;
    if (Array.isArray(existing) && existing.length > 0) {
        return renumber(
            [...existing].sort(
                (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
            )
        );
    }

    const kind = resolveIvrSchedulerPresentationKind(blockConfig);
    if (kind === "weekdays") {
        const days =
            (blockConfig.schedulerDays as {
                dayOfWeek: number;
                enabled: boolean;
                start: string;
                end: string;
            }[]) ?? [];
        const daySortOrder: Record<number, number> = {
            1: 0,
            2: 1,
            3: 2,
            4: 3,
            5: 4,
            6: 5,
            0: 6,
        };
        const enabled = days
            .filter(
                (d) =>
                    d.enabled &&
                    String(d.start ?? "").trim() &&
                    String(d.end ?? "").trim()
            )
            .sort(
                (a, b) =>
                    (daySortOrder[a.dayOfWeek] ?? 999) -
                    (daySortOrder[b.dayOfWeek] ?? 999)
            );
        return renumber(
            enabled.map((d) => ({
                priority: 0,
                daysMask: schedulerDayOfWeekToDaysMask(d.dayOfWeek),
                start: trimTimeToHHMM(d.start),
                end: trimTimeToHHMM(d.end),
                activeToId,
            }))
        );
    }

    if (kind === "daily") {
        const w = resolveSchedulerOnceExtraWindow(blockConfig);
        if (!w?.start?.trim() || !w?.end?.trim()) return [];
        return renumber(buildOnceSlotRowsFromExtraWindow(w, activeToId));
    }

    return [];
}

function expandSchedulerOnceDailyChain(
    configNodeId: string,
    config: Record<string, unknown>,
    configX: number,
    configY: number,
    configAccentColor: string
): { nodes: Node<HydrationBlockData>[]; edges: Edge[] } {
    const w = resolveSchedulerOnceExtraWindow(config);
    if (!w?.start?.trim() || !w?.end?.trim()) {
        return { nodes: [], edges: [] };
    }

    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const chainNodes: Node<HydrationBlockData>[] = [];
    const chainEdges: Edge[] = [];
    const y = ivrSchedulerSlotRowY(configY, IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT);
    const gap = IVR_SCHEDULER_SIBLING_GAP_PX;
    const primaryId = `${chainPrefix}daily-primary`;
    const primaryLabel = `${trimTimeToHHMM(w.start)} - ${trimTimeToHHMM(w.end)}`;
    const fullDay = schedulerOnceDailyIsFullDay(w.start, w.end);
    const childCount = fullDay ? 1 : 2;
    const totalWidth = Math.max(0, childCount - 1) * gap;
    const startX = configX - totalWidth / 2;

    chainNodes.push({
        id: primaryId,
        type: "ivrBlock",
        position: { x: startX, y },
        data: {
            label: primaryLabel,
            shape: "square",
            icon: "pi pi-calendar",
            accentColor: configAccentColor,
            isGenerated: true,
            outlineDashed: false,
            configParentId: configNodeId,
        },
    });
    chainEdges.push(makeEdge(configNodeId, primaryId));

    if (!fullDay) {
        const restId = `${chainPrefix}daily-rest`;
        const restLabel = schedulerOnceDailyRestLabel(
            w.start,
            w.end,
            w.start2,
            w.end2
        );
        chainNodes.push({
            id: restId,
            type: "ivrBlock",
            position: { x: startX + gap, y },
            data: {
                label: restLabel,
                shape: "square",
                icon: "pi pi-calendar",
                accentColor: configAccentColor,
                isGenerated: true,
                outlineDashed: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(configNodeId, restId));
    }

    return { nodes: chainNodes, edges: chainEdges };
}

function onceDailyChainNodeIds(
    chainP: string,
    config: Record<string, unknown>
): { primaryId: string; restId: string | null } {
    const primaryId = `${chainP}daily-primary`;
    const w = resolveSchedulerOnceExtraWindow(config);
    if (!w?.start?.trim() || !w?.end?.trim()) {
        return { primaryId, restId: null };
    }
    const fullDay = schedulerOnceDailyIsFullDay(w.start, w.end);
    return {
        primaryId,
        restId: fullDay ? null : `${chainP}daily-rest`,
    };
}

function onceSlotRowMatchesWindow(
    row: IvrSchedulerSlotRow,
    start: string,
    end: string
): boolean {
    return (
        trimTimeToHHMM(String(row.start ?? "")) === trimTimeToHHMM(start) &&
        trimTimeToHHMM(String(row.end ?? "")) === trimTimeToHHMM(end)
    );
}

/** Вузол ланцюжка Once (daily-primary / daily-rest) для рядка слота. */
function resolveOnceSchedulerChainNodeForRow(
    chainP: string,
    config: Record<string, unknown>,
    row: IvrSchedulerSlotRow,
    sortedSlots: IvrSchedulerSlotRow[]
): string {
    const { primaryId, restId } = onceDailyChainNodeIds(chainP, config);
    const w = resolveSchedulerOnceExtraWindow(config);
    if (
        w?.start2?.trim() &&
        w?.end2?.trim() &&
        restId &&
        onceSlotRowMatchesWindow(row, w.start2, w.end2)
    ) {
        return restId;
    }
    if (
        w?.start?.trim() &&
        w?.end?.trim() &&
        onceSlotRowMatchesWindow(row, w.start, w.end)
    ) {
        return primaryId;
    }
    const idx = sortedSlots.findIndex(
        (r) =>
            trimTimeToHHMM(r.start) === trimTimeToHHMM(row.start) &&
            trimTimeToHHMM(r.end) === trimTimeToHHMM(row.end)
    );
    if (idx === 1 && restId) return restId;
    return primaryId;
}

function resolveOnceSchedulerRouteChainSourceId(
    chainP: string,
    fromCfg: Record<string, unknown>,
    sortedSlots: IvrSchedulerSlotRow[],
    route: IvrApiRoute,
    routes: IvrApiRoute[],
    elementId: number
): string | null {
    const { primaryId, restId } = onceDailyChainNodeIds(chainP, fromCfg);
    const needOthers = schedulerSlotRowsNeedOthers(sortedSlots);
    const slotTargets = schedulerSlotJumpTargets(sortedSlots);
    const nt = normalizeRouteType(route.routeType);
    const isDefault =
        nt === "DEFAULT" && (route.routeValue ?? "").trim() === "";

    if (needOthers) {
        if (!isDefault && slotTargets.has(route.toElementId)) {
            return null;
        }
        if (isDefault) {
            return restId ?? primaryId;
        }
        const row = sortedSlots.find(
            (r) => r.activeToId === route.toElementId
        );
        if (row) {
            return resolveOnceSchedulerChainNodeForRow(
                chainP,
                fromCfg,
                row,
                sortedSlots
            );
        }
        return null;
    }

    if (isDefault) {
        const rowsToTarget = sortedSlots.filter(
            (r) => r.activeToId === route.toElementId
        );
        if (rowsToTarget.length === 1) {
            const onlyNode = resolveOnceSchedulerChainNodeForRow(
                chainP,
                fromCfg,
                rowsToTarget[0]!,
                sortedSlots
            );
            if (restId && sortedSlots.length > 1 && onlyNode === primaryId) {
                return restId;
            }
            return onlyNode;
        }
        if (rowsToTarget.length > 1) {
            return resolveOnceSchedulerChainNodeForRow(
                chainP,
                fromCfg,
                rowsToTarget[rowsToTarget.length - 1]!,
                sortedSlots
            );
        }
        return restId ?? primaryId;
    }

    if (slotTargets.has(route.toElementId)) {
        const row = sortedSlots.find(
            (r) => r.activeToId === route.toElementId
        );
        if (row) {
            return resolveOnceSchedulerChainNodeForRow(
                chainP,
                fromCfg,
                row,
                sortedSlots
            );
        }
    }
    return null;
}

function resolveSlotSchedulerRouteChainSourceId(
    chainP: string,
    sortedSlots: IvrSchedulerSlotRow[],
    route: IvrApiRoute,
    routes: IvrApiRoute[],
    elementId: number
): string | null {
    const needOthers = schedulerSlotRowsNeedOthers(sortedSlots);
    const slotTargets = schedulerSlotJumpTargets(sortedSlots);
    const nt = normalizeRouteType(route.routeType);
    const isDefault =
        nt === "DEFAULT" && (route.routeValue ?? "").trim() === "";

    if (needOthers) {
        if (!isDefault && slotTargets.has(route.toElementId)) {
            return null;
        }
        return `${chainP}sched-others`;
    }

    if (isDefault) {
        const row = sortedSlots.find(
            (r) => r.activeToId === route.toElementId
        );
        if (row) {
            const idx = sortedSlots.indexOf(row);
            return `${chainP}sched-slot-${ivrSchedulerSlotNodeIdSuffix(sortedSlots, idx)}`;
        }
        return null;
    }

    if (slotTargets.has(route.toElementId)) {
        const idx = sortedSlots.findIndex(
            (r) => r.activeToId === route.toElementId
        );
        if (idx >= 0) {
            return `${chainP}sched-slot-${ivrSchedulerSlotNodeIdSuffix(sortedSlots, idx)}`;
        }
    }
    return null;
}

function resolveDaysweekSimpleSchedulerRouteChainSourceId(
    chainP: string,
    config: Record<string, unknown>,
    sortedSlots: IvrSchedulerSlotRow[],
    route: IvrApiRoute
): string | null {
    const days =
        (config.schedulerDays as {
            dayOfWeek: number;
            enabled: boolean;
            start: string;
            end: string;
        }[]) ?? [];
    const enabledDays = days.filter(
        (d) => d.enabled && !!d.start && !!d.end
    );
    const needOthers = schedulerDaysweekSimpleNeedsOthers(enabledDays);
    const slotTargets = schedulerSlotJumpTargets(sortedSlots);
    const nt = normalizeRouteType(route.routeType);
    const isDefault =
        nt === "DEFAULT" && (route.routeValue ?? "").trim() === "";

    const dayNodeForRow = (row: IvrSchedulerSlotRow): string | null => {
        const meta = DAYS_MASK_ORDER.find(
            (d) => d.mask === (Number(row.daysMask) || 0)
        );
        if (!meta) return null;
        return `${chainP}day-${meta.dayOfWeek}`;
    };

    if (needOthers) {
        if (!isDefault && slotTargets.has(route.toElementId)) {
            return null;
        }
        return `${chainP}sched-others`;
    }
    if (isDefault) {
        const row = sortedSlots.find(
            (r) => r.activeToId === route.toElementId
        );
        return row ? dayNodeForRow(row) : null;
    }
    if (slotTargets.has(route.toElementId)) {
        const row = sortedSlots.find(
            (r) => r.activeToId === route.toElementId
        );
        return row ? dayNodeForRow(row) : null;
    }
    return null;
}

const NODE_GAP_VERTICAL = 68;
export const IVR_BASE_NODE_ID = "ivr-base";
/** @deprecated use IVR_BASE_NODE_ID */
const BASE_NODE_ID = IVR_BASE_NODE_ID;
export const IVR_START_CONFIG_PREFIX = "start-config-";
const START_CONFIG_PREFIX = IVR_START_CONFIG_PREFIX;
export const IVR_CHAIN_PREFIX = "start-chain-";
const CHAIN_PREFIX = IVR_CHAIN_PREFIX;

export const IVR_OPERATOR_QUEUE_CHAIN_SUFFIX = "operator-queue";

export function ivrOperatorQueueChainNodeId(
    startConfigGraphNodeId: string
): string {
    return `${CHAIN_PREFIX}${startConfigGraphNodeId}-${IVR_OPERATOR_QUEUE_CHAIN_SUFFIX}`;
}
const ALL_DTMF_KEYS: string[] = [...IVR_MENU_DTMF_KEY_ORDER];

function wrapLabelToLines(input: string, maxCharsPerLine: number, maxLines: number): string {
    const text = (input ?? "").trim();
    if (!text) return "";
    const words = text.split(/\s+/g);
    const lines: string[] = [];
    let current = "";
    for (const word of words) {
        const maybeSplitWord =
            word.length > maxCharsPerLine
                ? word.match(new RegExp(`.{1,${maxCharsPerLine}}`, "g")) ?? [word]
                : [word];
        for (const part of maybeSplitWord) {
            const next = current ? `${current} ${part}` : part;
            if (next.length <= maxCharsPerLine) {
                current = next;
                continue;
            }
            if (current) lines.push(current);
            current = part;
            if (lines.length >= maxLines) break;
        }
        if (lines.length >= maxLines) break;
    }
    if (current && lines.length < maxLines) lines.push(current);
    if (lines.length <= maxLines) return lines.join("\n");
    const kept = lines.slice(0, maxLines);
    const last = kept[kept.length - 1] ?? "";
    kept[kept.length - 1] =
        last.length > Math.max(0, maxCharsPerLine - 3)
            ? `${last.slice(0, maxCharsPerLine - 3)}...`
            : last;
    return kept.join("\n");
}

function makeEdge(
    source: string,
    target: string,
    sourceHandle = "bottom-source",
    targetHandle = "top",
    extraData?: Record<string, unknown> | null
): Edge {
    const base: Edge = {
        id: `e-${source}-${target}`,
        source,
        target,
        sourceHandle,
        targetHandle,
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: "#b1b1b7",
            width: 18,
            height: 18,
        },
    };
    const data: Record<string, unknown> = {
        ...(extraData ?? {}),
    };
    if (target.startsWith(START_CONFIG_PREFIX)) {
        data.ivrConfigurableLink = true;
    }
    return Object.keys(data).length ? { ...base, data } : base;
}

function normalizeEventRoute(routeValue: string): "timeout" | "invalid" | null {
    const v = routeValue.trim().toLowerCase();
    if (v === "timeout") return "timeout";
    if (v === "invalid" || v === "invalid input" || v === "other" || v === "others") return "invalid";
    return null;
}

function normalizeRouteType(routeType: string | null | undefined): string {
    return (routeType ?? "").trim().toUpperCase().replace(/-/g, "_");
}

function menuTailIdBeforeDtmf(
    configNodeId: string,
    chainPrefix: string,
    config: Record<string, unknown>
): string {
    let lastSourceId = configNodeId;
    const menuMeta =
        (config.menuAudioMeta as { audioId: number; audioName: string }[]) ?? [];
    const menuSilensPre = config.menuSilensPre as number | null;
    const menuSilensPost = config.menuSilensPost as number | null;
    if (menuSilensPre != null) {
        lastSourceId = `${chainPrefix}silens-pre`;
    }
    if (menuMeta.length > 0) {
        lastSourceId = `${chainPrefix}audio-${menuMeta.length - 1}`;
    }
    if (menuSilensPost != null) {
        lastSourceId = `${chainPrefix}silens-post`;
    }
    return lastSourceId;
}

function routesFromElementSorted(
    elementId: number,
    routes: IvrApiRoute[]
): IvrApiRoute[] {
    return routes
        .filter(
            (r) => r.fromElementId === elementId && r.isActive !== false
        )
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

function deriveMenuDtmfKeysFromRoutes(elementId: number, routes: IvrApiRoute[]): string[] {
    const keys = new Set<string>();
    for (const r of routesFromElementSorted(elementId, routes)) {
        const nt = normalizeRouteType(r.routeType);
        const raw = (r.routeValue ?? "").trim();
        if (!ALL_DTMF_KEYS.includes(raw)) continue;
        if (nt === "DTMF" || nt === "DTMF_EXCEEDED") {
            keys.add(raw);
            continue;
        }
        if (nt === "DEFAULT" && raw === "") continue;
        if (nt === "") {
            keys.add(raw);
        }
    }
    return Array.from(keys).sort(
        (a, b) => ALL_DTMF_KEYS.indexOf(a) - ALL_DTMF_KEYS.indexOf(b)
    );
}

function configPresentation(configType: string, startConfigTitle: string) {
    const title = (startConfigTitle ?? "").trim();
    const configLabelFallback = configType === "End" ? "end" : configType;
    const configLabel =
        configType === "End"
            ? title || "END"
            : title
              ? `${configLabelFallback}\n${title}`
              : configLabelFallback;
    const configIcon =
        configType === "End"
            ? "pi pi-stop"
            : configType === "Menu"
              ? "pi pi-list"
              : configType === "AgentHunter"
                ? "pi pi-database"
                : configType === "Scheduler"
                  ? "pi pi-calendar"
                  : "pi pi-play";
    const configAccentColor =
        configType === "Play"
            ? "rgb(15, 139, 253)"
            : configType === "AgentHunter"
              ? "rgb(255, 152, 0)"
              : configType === "Scheduler"
                ? "rgb(76, 175, 80)"
                : configType === "End"
                  ? "rgb(76, 175, 80)"
                  : "#9c27b0";
    return { configLabel, configIcon, configAccentColor };
}

function audioMetaFromApi(audios: IvrApiAudio[] | null | undefined) {
    const list = audios ?? [];
    return {
        ids: list.map((a) => a.audioId),
        meta: list.map((a) => ({
            audioId: a.audioId,
            audioName: a.audioName,
            groupId: a.groupId ?? null,
            ...(a.groupName != null ? { groupName: a.groupName } : {}),
        })),
    };
}

export function schedulerPreferFlatSlotRowChain(
    rows: IvrSchedulerSlotRow[]
): boolean {
    const perDay = new Map<number, number>();
    for (const row of rows) {
        const mask = Number(row.daysMask) || 0;
        perDay.set(mask, (perDay.get(mask) ?? 0) + 1);
    }
    for (const count of Array.from(perDay.values())) {
        if (count > 1) return false;
    }
    return true;
}

function expandSchedulerSlotChain(
    configNodeId: string,
    config: Record<string, unknown>,
    configX: number,
    configY: number,
    configAccentColor: string,
    ctx?: { elementId: number; routes: IvrApiRoute[] }
): { nodes: Node<HydrationBlockData>[]; edges: Edge[] } {
    const rows = [
        ...((config.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
            []),
    ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    if (!rows.length) return { nodes: [], edges: [] };

    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const chainNodes: Node<HydrationBlockData>[] = [];
    const chainEdges: Edge[] = [];
    const y = ivrSchedulerSlotRowY(configY, IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT);
    const gap = IVR_SCHEDULER_SIBLING_GAP_PX;
    const schedKind = normalizeIvrSchedulerScheduleKind(
        config.schedulerScheduleKind
    );
    const needOthersRaw =
        ctx != null
            ? schedulerChainNeedsOthers(rows, ctx.elementId, ctx.routes)
            : schedulerSlotRowsNeedOthers(rows);
    const needOthers =
        schedKind === "once_advanced" ? false : needOthersRaw;
    const childCount = rows.length + (needOthers ? 1 : 0);
    const totalWidth = Math.max(0, childCount - 1) * gap;
    const startX = configX - totalWidth / 2;

    rows.forEach((row, idx) => {
        const slotId = `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(rows, idx)}`;
        const label = formatSchedulerSlotNodeLabel(
            row.daysMask,
            row.start,
            row.end
        );
        chainNodes.push({
            id: slotId,
            type: "ivrBlock",
            position: { x: startX + idx * gap, y },
            data: {
                label: wrapLabelToLines(label, 22, 4),
                shape: "square",
                icon: "pi pi-calendar",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(configNodeId, slotId));
    });

    if (needOthers) {
        const othersId = `${chainPrefix}sched-others`;
        chainNodes.push({
            id: othersId,
            type: "ivrBlock",
            position: { x: startX + rows.length * gap, y },
            data: {
                label: "Others",
                shape: "square",
                icon: "pi pi-asterisk",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(configNodeId, othersId));
    }

    return { nodes: chainNodes, edges: chainEdges };
}

export function expandSchedulerDaysweekSimpleChain(
    configNodeId: string,
    config: Record<string, unknown>,
    configX: number,
    configY: number,
    configAccentColor: string,
    ctx?: { elementId: number; routes: IvrApiRoute[] }
): { nodes: Node<HydrationBlockData>[]; edges: Edge[] } {
    const days =
        (config.schedulerDays as {
            dayOfWeek: number;
            enabled: boolean;
            start: string;
            end: string;
        }[]) ?? [];
    const enabledDays = days.filter(
        (d) => d.enabled && !!String(d.start ?? "").trim() && !!String(d.end ?? "").trim()
    );
    if (!enabledDays.length) {
        return { nodes: [], edges: [] };
    }

    const daySortOrder: Record<number, number> = {
        1: 0,
        2: 1,
        3: 2,
        4: 3,
        5: 4,
        6: 5,
        0: 6,
    };
    const enabledDaysSorted = [...enabledDays].sort(
        (a, b) =>
            (daySortOrder[a.dayOfWeek] ?? 999) -
            (daySortOrder[b.dayOfWeek] ?? 999)
    );
    const needOthers =
        ctx != null
            ? schedulerChainNeedsOthers(
                  buildSchedulerSlotRowsFromBlockConfig(
                      config,
                      Number(config.schedulerActiveToId) || 0
                  ),
                  ctx.elementId,
                  ctx.routes
              )
            : schedulerDaysweekSimpleNeedsOthers(enabledDaysSorted);

    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const chainNodes: Node<HydrationBlockData>[] = [];
    const chainEdges: Edge[] = [];
    const y = ivrSchedulerSlotRowY(configY, IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT);
    const gap = IVR_SCHEDULER_SIBLING_GAP_PX;
    const childCount = enabledDaysSorted.length + (needOthers ? 1 : 0);
    const totalWidth = Math.max(0, childCount - 1) * gap;
    const startX = configX - totalWidth / 2;

    for (let i = 0; i < enabledDaysSorted.length; i++) {
        const d = enabledDaysSorted[i]!;
        const meta = DAYS_MASK_ORDER.find((m) => m.dayOfWeek === d.dayOfWeek);
        const id = `${chainPrefix}day-${d.dayOfWeek}`;
        const label = `${meta?.short ?? d.dayOfWeek}\n${trimTimeToHHMM(d.start)} - ${trimTimeToHHMM(d.end)}`;
        chainNodes.push({
            id,
            type: "ivrBlock",
            position: { x: startX + i * gap, y },
            data: {
                label: wrapLabelToLines(label, 22, 4),
                shape: "square",
                icon: "pi pi-calendar",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(configNodeId, id));
    }

    if (needOthers) {
        const othersId = `${chainPrefix}sched-others`;
        chainNodes.push({
            id: othersId,
            type: "ivrBlock",
            position: { x: startX + enabledDaysSorted.length * gap, y },
            data: {
                label: "Others",
                shape: "square",
                icon: "pi pi-asterisk",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(configNodeId, othersId));
    }

    return { nodes: chainNodes, edges: chainEdges };
}

function schedulerDaysweekSimpleNeedsOthers(
    enabledDays: { start: string; end: string }[]
): boolean {
    if (enabledDays.length < 7) return true;
    return enabledDays.some((d) => {
        const s = String(d.start ?? "").trim();
        const e = String(d.end ?? "").trim();
        const full24 =
            s === "00:00" &&
            (e === "23:59" || e === "00:00" || e === "24:00");
        return !full24;
    });
}

/** Id вузлів ланцюга Scheduler, з яких дозволені вихідні ребра після generateChain. */
export function collectIvrSchedulerOutboundLinkSourceIds(
    configNodeId: string,
    config: Record<string, unknown>
): Set<string> {
    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const ids = new Set<string>();

    const slotRows = [
        ...((config.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
            []),
    ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    const schedKind = normalizeIvrSchedulerScheduleKind(
        config.schedulerScheduleKind
    );
    const presentationKind = resolveIvrSchedulerPresentationKind(config);

    if (schedKind === "once" || presentationKind === "daily") {
        const w = resolveSchedulerOnceExtraWindow(config);
        if (w?.start?.trim() && w?.end?.trim()) {
            ids.add(`${chainPrefix}daily-primary`);
            if (!schedulerOnceDailyIsFullDay(w.start, w.end)) {
                ids.add(`${chainPrefix}daily-rest`);
            }
        }
        return ids;
    }

    if (schedKind === "daysweek") {
        const days =
            (config.schedulerDays as {
                dayOfWeek: number;
                enabled: boolean;
                start: string;
                end: string;
            }[]) ?? [];
        const enabledDays = days.filter(
            (d) => d.enabled && !!d.start && !!d.end
        );
        const daySortOrder: Record<number, number> = {
            1: 0,
            2: 1,
            3: 2,
            4: 3,
            5: 4,
            6: 5,
            0: 6,
        };
        const enabledDaysSorted = [...enabledDays].sort(
            (a, b) =>
                (daySortOrder[a.dayOfWeek] ?? 999) -
                (daySortOrder[b.dayOfWeek] ?? 999)
        );
        for (const d of enabledDaysSorted) {
            ids.add(`${chainPrefix}day-${d.dayOfWeek}`);
        }
        if (schedulerDaysweekSimpleNeedsOthers(enabledDaysSorted)) {
            ids.add(`${chainPrefix}sched-others`);
        }
        return ids;
    }

    if (slotRows.length > 0) {
        const useAdvancedTree =
            schedKind === "daysweek_advanced" &&
            !schedulerPreferFlatSlotRowChain(slotRows);
        if (useAdvancedTree) {
            const needOthers = schedulerSlotRowsNeedOthers(slotRows);
            const columns = ivrBuildSchedulerAdvancedColumns(
                slotRows,
                needOthers
            );
            for (const col of columns) {
                if (col.kind === "day") {
                    for (const slot of col.slots) {
                        ids.add(
                            `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(
                                slotRows,
                                slot.idx
                            )}`
                        );
                    }
                } else {
                    ids.add(`${chainPrefix}sched-others`);
                }
            }
        } else {
            for (let si = 0; si < slotRows.length; si++) {
                ids.add(
                    `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(
                        slotRows,
                        si
                    )}`
                );
            }
            if (
                schedulerSlotRowsNeedOthers(slotRows) &&
                schedKind !== "once_advanced"
            ) {
                ids.add(`${chainPrefix}sched-others`);
            }
        }
        return ids;
    }

    const days =
        (config.schedulerDays as {
            dayOfWeek: number;
            enabled: boolean;
            start: string;
            end: string;
        }[]) ?? [];
    const enabledDays = days.filter(
        (d) => d.enabled && !!d.start && !!d.end
    );
    const daySortOrder: Record<number, number> = {
        1: 0,
        2: 1,
        3: 2,
        4: 3,
        5: 4,
        6: 5,
        0: 6,
    };
    const enabledDaysSorted = [...enabledDays].sort(
        (a, b) =>
            (daySortOrder[a.dayOfWeek] ?? 999) -
            (daySortOrder[b.dayOfWeek] ?? 999)
    );
    for (const d of enabledDaysSorted) {
        ids.add(`${chainPrefix}day-${d.dayOfWeek}`);
    }
    if (schedulerDaysweekSimpleNeedsOthers(enabledDaysSorted)) {
        ids.add(`${chainPrefix}sched-others`);
    }
    return ids;
}

const DAYS_WEEK_ADVANCED_DAY_SORT: Record<number, number> = {
    1: 0,
    2: 1,
    3: 2,
    4: 3,
    5: 4,
    6: 5,
    0: 6,
};

export type IvrSchedulerAdvancedColumn =
    | {
          kind: "day";
          dayOfWeek: number;
          label: string;
          slots: { row: IvrSchedulerSlotRow; idx: number }[];
      }
    | { kind: "others" };

export function ivrBuildSchedulerAdvancedColumns(
    sorted: IvrSchedulerSlotRow[],
    needOthers: boolean
): IvrSchedulerAdvancedColumn[] {
    const byDay = new Map<number, { row: IvrSchedulerSlotRow; idx: number }[]>();
    for (let idx = 0; idx < sorted.length; idx++) {
        const row = sorted[idx]!;
        const mask = Number(row.daysMask) || 0;
        const meta = DAYS_MASK_ORDER.find((d) => d.mask === mask);
        if (!meta) continue;
        const arr = byDay.get(meta.dayOfWeek) ?? [];
        arr.push({ row, idx });
        byDay.set(meta.dayOfWeek, arr);
    }

    const activeDayOfWeeks = Array.from(byDay.keys()).sort(
        (a, b) =>
            (DAYS_WEEK_ADVANCED_DAY_SORT[a] ?? 999) -
            (DAYS_WEEK_ADVANCED_DAY_SORT[b] ?? 999)
    );

    const columns: IvrSchedulerAdvancedColumn[] = activeDayOfWeeks.map(
        (dow) => {
            const meta = DAYS_MASK_ORDER.find((d) => d.dayOfWeek === dow);
            return {
                kind: "day" as const,
                dayOfWeek: dow,
                label: meta?.short ?? String(dow),
                slots: byDay.get(dow) ?? [],
            };
        }
    );
    if (needOthers) {
        columns.push({ kind: "others" });
    }
    return columns;
}

export function expandSchedulerDaysweekAdvancedChain(
    configNodeId: string,
    config: Record<string, unknown>,
    configX: number,
    configY: number,
    configAccentColor: string,
    ctx?: { elementId: number; routes: IvrApiRoute[] }
): { nodes: Node<HydrationBlockData>[]; edges: Edge[] } {
    const sorted = [
        ...((config.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
            []),
    ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    if (!sorted.length) return { nodes: [], edges: [] };

    const needOthers =
        ctx != null
            ? schedulerChainNeedsOthers(sorted, ctx.elementId, ctx.routes)
            : schedulerSlotRowsNeedOthers(sorted);

    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const chainNodes: Node<HydrationBlockData>[] = [];
    const chainEdges: Edge[] = [];
    const yDay = ivrSchedulerSlotRowY(configY, IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT);
    const colGap = 0;
    const nodeLeft = (centerX: number) => centerX - IVR_NODE_WIDTH / 2;

    const columns = ivrBuildSchedulerAdvancedColumns(sorted, needOthers);
    if (!columns.length) return { nodes: [], edges: [] };

    const colWidths = columns.map((c) =>
        c.kind === "day"
            ? ivrSchedulerAdvancedColumnWidth(c.slots.length)
            : IVR_SCHEDULER_SIBLING_GAP_PX
    );
    const totalWidth =
        colWidths.reduce((s, w) => s + w, 0) +
        Math.max(0, columns.length - 1) * colGap;
    let xCursor = configX - totalWidth / 2;

    for (let ci = 0; ci < columns.length; ci++) {
        const col = columns[ci]!;
        const colW = colWidths[ci] ?? IVR_SCHEDULER_SIBLING_GAP_PX;
        const centerX = xCursor + colW / 2;

        if (col.kind === "day") {
            const dayId = `${chainPrefix}day-${col.dayOfWeek}`;
            chainNodes.push({
                id: dayId,
                type: "ivrBlock",
                position: { x: nodeLeft(centerX), y: yDay },
                data: {
                    label: col.label,
                    shape: "square",
                    icon: "pi pi-calendar",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainEdges.push(makeEdge(configNodeId, dayId));

            const slotCount = col.slots.length;
            col.slots.forEach((slot, si) => {
                const slotId = `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(
                    sorted,
                    slot.idx
                )}`;
                const timeLabel = `${trimTimeToHHMM(String(slot.row.start ?? ""))} - ${trimTimeToHHMM(String(slot.row.end ?? ""))}`;
                const slotPos = ivrSchedulerDaySlotRowLayout(
                    yDay,
                    centerX,
                    si,
                    slotCount
                );
                chainNodes.push({
                    id: slotId,
                    type: "ivrBlock",
                    position: slotPos,
                    data: {
                        label: wrapLabelToLines(timeLabel, 22, 4),
                        shape: "square",
                        icon: "pi pi-calendar",
                        accentColor: configAccentColor,
                        isGenerated: true,
                        outlineDashed: false,
                        configParentId: configNodeId,
                    },
                });
                chainEdges.push(makeEdge(dayId, slotId));
            });
        } else {
            const othersId = `${chainPrefix}sched-others`;
            chainNodes.push({
                id: othersId,
                type: "ivrBlock",
                position: { x: nodeLeft(centerX), y: yDay },
                data: {
                    label: "Others",
                    shape: "square",
                    icon: "pi pi-asterisk",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainEdges.push(makeEdge(configNodeId, othersId));
        }

        xCursor += colW + colGap;
    }

    return { nodes: chainNodes, edges: chainEdges };
}

function apiElementIdentity(el: IvrApiElement): Record<string, unknown> {
    return {
        elementId: el.elementId,
        flowId: el.flowId,
        elementType: el.elementType,
        name: el.name,
    };
}

function menuRetryFromDraft(value: unknown): number | null {
    if (value == null || value === "") return null;
    const n = typeof value === "number" ? value : Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
}

function mergeMenuBlockConfigFromDraft(
    cfg: Record<string, unknown>,
    draftRaw: unknown
): Record<string, unknown> {
    const d = parseIvrElementDraftData(draftRaw);
    if (!d) return cfg;
    const next = { ...cfg };
    if (Array.isArray(d.menuDtmfKeys)) {
        next.menuDtmfKeys = sortIvrMenuDtmfKeys(d.menuDtmfKeys);
    }
    if (d.menuDtmfRetries && typeof d.menuDtmfRetries === "object") {
        next.menuDtmfRetries = {
            ...((cfg.menuDtmfRetries as Record<string, number | null>) ?? {}),
            ...(d.menuDtmfRetries as Record<string, number | null>),
        };
    }
    if (Object.prototype.hasOwnProperty.call(d, "menuTimeoutRetries")) {
        next.menuTimeoutRetries = menuRetryFromDraft(d.menuTimeoutRetries);
    }
    if (Object.prototype.hasOwnProperty.call(d, "menuRetries")) {
        next.menuRetries = menuRetryFromDraft(d.menuRetries);
    }
    return next;
}

function mergeSchedulerBlockConfigFromDraft(
    cfg: Record<string, unknown>,
    draftRaw: unknown
): Record<string, unknown> {
    const d = parseIvrElementDraftData(draftRaw);
    if (!d) return cfg;
    const next = { ...cfg };
    const r = d as Record<string, unknown>;

    const kindRaw =
        typeof r.schedulerScheduleKind === "string"
            ? r.schedulerScheduleKind
            : typeof r.SchedulerScheduleKind === "string"
              ? r.SchedulerScheduleKind
              : undefined;
    if (typeof kindRaw === "string") {
        next.schedulerScheduleKind =
            normalizeIvrSchedulerScheduleKind(kindRaw);
    }

    const draftKindNorm =
        typeof next.schedulerScheduleKind === "string"
            ? normalizeIvrSchedulerScheduleKind(next.schedulerScheduleKind)
            : null;

    const daysRaw =
        (Array.isArray(r.schedulerDays) ? r.schedulerDays : null) ??
        (Array.isArray(r.SchedulerDays) ? r.SchedulerDays : null);
    if (Array.isArray(daysRaw) && daysRaw.length > 0) {
        const skipEmptyShell =
            draftKindNorm === "daysweek" &&
            isSchedulerDraftWeekdayEmptyShell(
                daysRaw as {
                    dayOfWeek: number;
                    enabled?: boolean;
                    start?: string;
                    end?: string;
                }[]
            );
        if (!skipEmptyShell) {
            if (
                draftKindNorm === "daysweek" &&
                daysRaw.length < 7 &&
                Array.isArray(next.schedulerDays) &&
                (next.schedulerDays as unknown[]).length > 0
            ) {
                next.schedulerDays = mergeDaysweekSchedulerDaySources(
                    next.schedulerDays as {
                        dayOfWeek: number;
                        enabled?: boolean;
                        start?: string;
                        end?: string;
                        label?: string;
                    }[],
                    daysRaw as {
                        dayOfWeek: number;
                        enabled?: boolean;
                        start?: string;
                        end?: string;
                        label?: string;
                    }[]
                );
            } else {
                next.schedulerDays = daysRaw;
            }
        }
    }

    const extraRaw = r.schedulerExtraWindow ?? r.SchedulerExtraWindow;
    if (extraRaw !== undefined) {
        next.schedulerExtraWindow = extraRaw;
    }

    const activeRaw = r.schedulerActiveToId ?? r.SchedulerActiveToId;
    if (activeRaw != null) {
        const n =
            typeof activeRaw === "number" ? activeRaw : Number(activeRaw);
        if (Number.isFinite(n)) next.schedulerActiveToId = n;
    }

    const slotRowsDraft =
        (Array.isArray(r.schedulerSlotRows) ? r.schedulerSlotRows : null) ??
        (Array.isArray(r.SchedulerSlotRows) ? r.SchedulerSlotRows : null);
    if (
        Array.isArray(slotRowsDraft) &&
        slotRowsDraft.length > 0 &&
        draftKindNorm !== "daysweek"
    ) {
        next.schedulerSlotRows = slotRowsDraft as IvrSchedulerSlotRow[];
    }
    if (draftKindNorm === "daysweek") {
        delete next.schedulerSlotRows;
    }
    if (draftKindNorm === "once_advanced") {
        next.schedulerShowOthers = false;
    }

    const showOthersRaw = r.schedulerShowOthers ?? r.SchedulerShowOthers;
    if (
        typeof showOthersRaw === "boolean" &&
        draftKindNorm !== "once_advanced"
    ) {
        next.schedulerShowOthers = showOthersRaw;
    }

    return next;
}

export function apiElementDraftDataRaw(
    el: IvrApiElement | null | undefined
): unknown {
    if (el == null || typeof el !== "object") {
        return undefined;
    }
    const r = el as Record<string, unknown>;
    return r.draftData ?? r.DraftData;
}

const SCHEDULER_CLIENT_DRAFT_OVERLAY_KEYS = [
    "schedulerScheduleKind",
    "schedulerExtraWindow",
    "schedulerActiveToId",
    "schedulerDays",
    "schedulerSlotRows",
] as const;

export function augmentSchedulerApiElementWithClientBlockConfig(
    apiEl: IvrApiElement,
    clientBlockConfig: Record<string, unknown> | null | undefined
): IvrApiElement {
    if (!clientBlockConfig) return apiEl;
    const pt = String(apiEl.elementType ?? "").toUpperCase();
    if (pt !== "SCHEDULER" && pt !== "SCHEDULE") return apiEl;

    const prevObj = parseIvrElementDraftData(apiElementDraftDataRaw(apiEl));
    const base: Record<string, unknown> =
        prevObj != null && typeof prevObj === "object"
            ? { ...prevObj }
            : { schemaVersion: 1 };

    let touched = false;
    for (const k of SCHEDULER_CLIENT_DRAFT_OVERLAY_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(clientBlockConfig, k)) {
            continue;
        }
        const v = clientBlockConfig[k];
        if (v === undefined) continue;

        let prevSer = "";
        try {
            prevSer = JSON.stringify(base[k]);
        } catch {
            prevSer = "";
        }
        let nextSer = "";
        try {
            nextSer = JSON.stringify(v);
        } catch {
            continue;
        }
        if (prevSer !== nextSer) touched = true;
        base[k] = v;
    }

    if (!touched) return apiEl;

    let draftStr: string;
    try {
        draftStr = JSON.stringify(base);
    } catch {
        return apiEl;
    }

    const out: Record<string, unknown> = {
        ...(apiEl as Record<string, unknown>),
        draftData: draftStr,
    };
    delete out.DraftData;
    return out as IvrApiElement;
}

export function elementToBlockConfig(
    el: IvrApiElement,
    routes: IvrApiRoute[],
    schedules?: IvrApiSchedule[]
): Record<string, unknown> {
    const type = (el.elementType ?? "").toUpperCase();
    const title = (el.name ?? "").trim();
    const base: Record<string, unknown> = {
        startConfigTitle: title,
        configSourceNodeId: BASE_NODE_ID,
        schedulerDays: [],
        hunterQueueIds: [],
        hunterQueueMeta: [],
    };

    if (type === "END") {
        return {
            ...base,
            ...apiElementIdentity(el),
            startConfiguration: "End",
            audios: el.audios,
            elementData: el.elementData,
            minDigits: el.minDigits ?? null,
            maxDigits: el.maxDigits ?? null,
            terminators: el.terminators ?? null,
            regexp: el.regexp ?? null,
            digitTimeOut: el.digitTimeOut ?? null,
            timeOut: el.timeOut ?? null,
            silencePre: el.silencePre ?? null,
            silencePost: el.silencePost ?? null,
        };
    }

    if (type === "MENU") {
        const { ids, meta } = audioMetaFromApi(el.audios);
        const menuDtmfKeys = deriveMenuDtmfKeysFromRoutes(el.elementId, routes);
        const menuDtmfRetries: Record<string, number | null> = {};
        let menuTimeoutRetries: number | null = null;
        let menuRetries: number | null = null;
        const sortedMenuRoutes = routesFromElementSorted(el.elementId, routes);
        for (const r of sortedMenuRoutes) {
            const raw = (r.routeValue ?? "").trim();
            const nt = normalizeRouteType(r.routeType);
            if (nt === "DTMF_EXCEEDED" && ALL_DTMF_KEYS.includes(raw) && r.tries != null) {
                menuDtmfRetries[raw] = r.tries;
            }
        }
        for (const r of sortedMenuRoutes) {
            const raw = (r.routeValue ?? "").trim();
            const nt = normalizeRouteType(r.routeType);
            if (nt !== "") continue;
            if (!ALL_DTMF_KEYS.includes(raw) || r.tries == null) continue;
            if (menuDtmfRetries[raw] == null) {
                menuDtmfRetries[raw] = r.tries;
            }
        }
        for (const r of sortedMenuRoutes) {
            if (normalizeRouteType(r.routeType) !== "EVENT_EXCEEDED") continue;
            const ev = normalizeEventRoute((r.routeValue ?? "").trim());
            if (ev === "timeout" && r.tries != null && menuTimeoutRetries == null) {
                menuTimeoutRetries = r.tries;
            }
            if (ev === "invalid" && r.tries != null && menuRetries == null) {
                menuRetries = r.tries;
            }
        }
        return mergeMenuBlockConfigFromDraft(
            {
                ...base,
                ...apiElementIdentity(el),
                startConfiguration: "Menu",
                menuAudioGroupId: null,
                menuAudioIds: ids,
                menuAudioMeta: meta,
                menuSilensPre: el.silencePre ?? null,
                menuSilensPost: el.silencePost ?? null,
                menuTimeout: el.timeOut ?? null,
                menuTimeoutRetries,
                menuRetries,
                menuDtmfKeys,
                menuDtmfRetries,
            },
            apiElementDraftDataRaw(el)
        );
    }

    if (type === "PLAY") {
        const { ids, meta } = audioMetaFromApi(el.audios);
        return {
            ...base,
            ...apiElementIdentity(el),
            startConfiguration: "Play",
            playAudioGroupId: null,
            playAudioIds: ids,
            playAudioMeta: meta,
            playSilensPre: el.silencePre ?? null,
            playSilensPost: el.silencePost ?? null,
            playTimeout: el.timeOut ?? null,
        };
    }

    if (
        type === "AGENTHUNTER" ||
        type === "HUNTER" ||
        type === "AGENT_HUNTER" ||
        type === "OPERATOR"
    ) {
        const rawData = el.elementData;
        const parsed =
            rawData == null || rawData === ""
                ? NaN
                : typeof rawData === "number"
                  ? rawData
                  : Number(String(rawData).trim());
        const hunterQueueIds =
            Number.isFinite(parsed) && parsed > 0 ? [parsed] : [];
        return {
            ...base,
            ...apiElementIdentity(el),
            startConfiguration: "AgentHunter",
            hunterQueueIds,
            hunterQueueMeta: [],
        };
    }

    if (type === "SCHEDULER" || type === "SCHEDULE") {
        const draftRaw = apiElementDraftDataRaw(el);
        const draftParsed = parseIvrElementDraftData(draftRaw);
        const draftKindFromEl =
            typeof draftParsed?.schedulerScheduleKind === "string"
                ? normalizeIvrSchedulerScheduleKind(
                      draftParsed.schedulerScheduleKind
                  )
                : typeof (draftParsed as Record<string, unknown> | null)
                        ?.SchedulerScheduleKind === "string"
                  ? normalizeIvrSchedulerScheduleKind(
                        (draftParsed as Record<string, unknown>)
                            .SchedulerScheduleKind
                    )
                  : null;

        const apiRows = schedulesForElement(el.elementId, schedules);
        const allSlotRows = apiSchedulesToSlotRows(apiRows);
        const kindForFilter = draftKindFromEl ?? "daysweek";
        const slotRows = filterApiSlotRowsForSchedulerKind(
            allSlotRows,
            kindForFilter
        );
        let schedulerDaysBaseline = expandSlotRowsToSchedulerDays(slotRows);
        let merged = mergeSchedulerBlockConfigFromDraft(
            {
                ...base,
                ...apiElementIdentity(el),
                startConfiguration: "Scheduler",
                schedulerScheduleKind: "daysweek",
                schedulerDays: schedulerDaysBaseline,
                schedulerSlotRows: slotRows,
                schedulerActiveToId:
                    slotRows.length > 0 ? slotRows[0]?.activeToId ?? null : null,
                schedulerShowOthers:
                    slotRows.length > 0
                        ? schedulerChainNeedsOthers(
                              slotRows,
                              el.elementId,
                              routes
                          )
                        : false,
            },
            draftRaw
        );

        const draftDaysOnly = merged.schedulerDays as
            | {
                  dayOfWeek: number;
                  enabled?: boolean;
                  start?: string;
                  end?: string;
              }[]
            | undefined;

        const kindNorm = normalizeIvrSchedulerScheduleKind(
            merged.schedulerScheduleKind
        );

        merged = {
            ...merged,
            schedulerDays:
                kindNorm === "daysweek"
                    ? mergeDaysweekSchedulerDaySources(
                          schedulerDaysBaseline,
                          draftDaysOnly
                      ).map((d) => ({
                          dayOfWeek: d.dayOfWeek,
                          label:
                              schedulerDaysBaseline.find(
                                  (x) => x.dayOfWeek === d.dayOfWeek
                              )?.label ?? "",
                          enabled: !!d.enabled,
                          start: d.start ?? "",
                          end: d.end ?? "",
                      }))
                    : mergeSchedulerWeekdaysFromApiRowsAndDraft(
                          slotRows.length > 0 ? slotRows : allSlotRows,
                          draftDaysOnly,
                          false
                      ),
        };

        let finalRows = mergeApiSchedulerSlotRowsWithDraftWeekdays(
            slotRows,
            merged
        );
        if (finalRows.length === 0 || kindNorm === "once") {
            const rawAid = merged.schedulerActiveToId;
            const aid =
                typeof rawAid === "number" ? rawAid : Number(rawAid);
            const activeToId =
                Number.isFinite(aid) && aid >= 0 ? aid : 0;
            const fromConfig = buildSchedulerSlotRowsFromBlockConfig(
                merged,
                activeToId
            );
            if (fromConfig.length > 0) {
                finalRows = fromConfig;
            }
        }

        const extraResolved = resolveSchedulerOnceExtraWindow({
            ...merged,
            schedulerSlotRows: finalRows,
        });
        if (extraResolved?.start?.trim() && extraResolved?.end?.trim()) {
            merged = {
                ...merged,
                schedulerExtraWindow: extraResolved,
            };
        }

        if (kindNorm === "daysweek") {
            finalRows = finalRows.filter((r) => {
                const m = Number(r.daysMask) || 0;
                return (
                    m !== SCHEDULER_ALL_DAYS_MASK && isSingleWeekdayDaysMask(m)
                );
            });
        }

        merged = {
            ...merged,
            schedulerScheduleKind: kindNorm,
            schedulerSlotRows: finalRows,
            schedulerShowOthers:
                kindNorm === "once_advanced"
                    ? false
                    : kindNorm === "once"
                      ? schedulerSlotRowsNeedOthers(finalRows)
                      : kindNorm === "daysweek_advanced"
                        ? schedulerSlotRowsNeedOthers(finalRows)
                        : schedulerChainNeedsOthers(
                              finalRows,
                              el.elementId,
                              routes
                          ),
        };

        return merged;
    }

    const { ids, meta } = audioMetaFromApi(el.audios);
    return {
        ...base,
        ...apiElementIdentity(el),
        startConfiguration: "Play",
        playAudioGroupId: null,
        playAudioIds: ids,
        playAudioMeta: meta,
        playSilensPre: el.silencePre ?? null,
        playSilensPost: el.silencePost ?? null,
        playTimeout: el.timeOut ?? null,
    };
}

function expandMenuChain(
    configNodeId: string,
    config: Record<string, unknown>,
    configX: number,
    configY: number,
    configAccentColor: string
): { nodes: Node<HydrationBlockData>[]; edges: Edge[] } {
    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const chainNodes: Node<HydrationBlockData>[] = [];
    const chainEdges: Edge[] = [];

    const menuMeta =
        (config.menuAudioMeta as { audioId: number; audioName: string }[]) ?? [];
    const menuSilensPre = config.menuSilensPre as number | null;
    const menuSilensPost = config.menuSilensPost as number | null;
    const menuRetries = config.menuRetries as number | null;
    const menuTimeout = config.menuTimeout as number | null;
    const menuTimeoutRetries = config.menuTimeoutRetries as number | null;
    const menuDtmfRetries =
        (config.menuDtmfRetries as Record<string, number | null | undefined> | undefined) ?? {};
    const menuDtmfKeys = (config.menuDtmfKeys as string[] | undefined) ?? [];
    const allDtmfSelected = menuDtmfKeys.length >= ALL_DTMF_KEYS.length;

    let lastSourceId = configNodeId;
    let y = configY;

    if (menuSilensPre != null) {
        const preId = `${chainPrefix}silens-pre`;
        y += IVR_PLAY_CHAIN_VERTICAL_GAP;
        chainNodes.push({
            id: preId,
            type: "ivrBlock",
            position: { x: configX, y },
            data: {
                label: `Silens Pre\n${menuSilensPre}`,
                shape: "square",
                icon: "pi pi-clock",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(lastSourceId, preId));
        lastSourceId = preId;
    }

    for (let i = 0; i < menuMeta.length; i++) {
        const audioNodeId = `${chainPrefix}audio-${i}`;
        y += IVR_PLAY_CHAIN_VERTICAL_GAP;
        chainNodes.push({
            id: audioNodeId,
            type: "ivrBlock",
            position: { x: configX, y },
            data: {
                label: wrapLabelToLines(menuMeta[i].audioName, 18, 3),
                shape: "triangle",
                icon: "pi pi-play",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(lastSourceId, audioNodeId));
        lastSourceId = audioNodeId;
    }

    if (menuSilensPost != null) {
        const postId = `${chainPrefix}silens-post`;
        y += IVR_PLAY_CHAIN_VERTICAL_GAP;
        chainNodes.push({
            id: postId,
            type: "ivrBlock",
            position: { x: configX, y },
            data: {
                label: `Silens Post\n${menuSilensPost}`,
                shape: "square",
                icon: "pi pi-clock",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(lastSourceId, postId));
        lastSourceId = postId;
    }

    const dtmfParentId = `${chainPrefix}dtmf`;
    const dtmfY = y + IVR_MENU_DTMF_VERTICAL_GAP;
    chainNodes.push({
        id: dtmfParentId,
        type: "ivrBlock",
        position: { x: configX, y: dtmfY },
        data: {
            label: "DTMF введення",
            shape: "square",
            icon: "pi pi-th-large",
            accentColor: configAccentColor,
            isGenerated: true,
            configParentId: configNodeId,
        },
    });
    chainEdges.push(makeEdge(lastSourceId, dtmfParentId));

    const dtmfChildrenLabels: string[] = [...menuDtmfKeys];
    if (!allDtmfSelected) dtmfChildrenLabels.push("Others");
    if (menuTimeout != null) dtmfChildrenLabels.push("TimeOut");
    const dtmfGap = IVR_MENU_DTMF_GAP_PX;
    const dtmfTotalWidth = (dtmfChildrenLabels.length - 1) * dtmfGap;
    const dtmfStartX = configX - dtmfTotalWidth / 2;
    const dtmfChildY = dtmfY + IVR_MENU_DTMF_VERTICAL_GAP;

    for (let i = 0; i < dtmfChildrenLabels.length; i++) {
        const label = dtmfChildrenLabels[i];
        const isSelectedKey = menuDtmfKeys.includes(label);
        const childId = `${chainPrefix}dtmf-${
            label === "TimeOut" ? "timeout" : label === "Others" ? "others" : label
        }`;
        chainNodes.push({
            id: childId,
            type: "ivrBlock",
            position: { x: dtmfStartX + i * dtmfGap, y: dtmfChildY },
            data: {
                label,
                shape: "square",
                icon: isSelectedKey ? "pi pi-th-large" : "pi pi-asterisk",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(dtmfParentId, childId));

        const x = dtmfStartX + i * dtmfGap;
        if (isSelectedKey) {
            const retriesVal = menuDtmfRetries[label];
            if (retriesVal != null) {
                const retriesId = `${chainPrefix}retries-${label}`;
                const retriesTrueId = `${chainPrefix}retries-${label}-true`;
                const retriesFalseId = `${chainPrefix}retries-${label}-false`;
                const branch = ivrMenuRetriesBranchLayout(x, dtmfChildY);
                chainNodes.push({
                    id: retriesId,
                    type: "ivrBlock",
                    position: branch.retries,
                    data: {
                        label: `Retries\n${retriesVal}`,
                        shape: "square",
                        icon: "pi pi-refresh",
                        accentColor: configAccentColor,
                        isGenerated: true,
                        configParentId: configNodeId,
                    },
                });
                chainNodes.push({
                    id: retriesTrueId,
                    type: "ivrBlock",
                    position: branch.trueNode,
                    data: {
                        label: "true",
                        shape: "square",
                        icon: "pi pi-check",
                        accentColor: configAccentColor,
                        isGenerated: true,
                        configParentId: configNodeId,
                    },
                });
                chainNodes.push({
                    id: retriesFalseId,
                    type: "ivrBlock",
                    position: branch.falseNode,
                    data: {
                        label: "false",
                        shape: "square",
                        icon: "pi pi-times",
                        accentColor: configAccentColor,
                        isGenerated: true,
                        configParentId: configNodeId,
                    },
                });
                chainEdges.push(makeEdge(childId, retriesId));
                chainEdges.push(makeEdge(retriesId, retriesTrueId));
                chainEdges.push(makeEdge(retriesId, retriesFalseId));
            }
        } else if (label === "TimeOut" && menuTimeoutRetries != null) {
            const retriesId = `${chainPrefix}timeout-retries`;
            const retriesTrueId = `${chainPrefix}timeout-retries-true`;
            const retriesFalseId = `${chainPrefix}timeout-retries-false`;
            const branch = ivrMenuRetriesBranchLayout(x, dtmfChildY);
            chainNodes.push({
                id: retriesId,
                type: "ivrBlock",
                position: branch.retries,
                data: {
                    label: `Retries\n${menuTimeoutRetries}`,
                    shape: "square",
                    icon: "pi pi-refresh",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainNodes.push({
                id: retriesTrueId,
                type: "ivrBlock",
                position: branch.trueNode,
                data: {
                    label: "true",
                    shape: "square",
                    icon: "pi pi-check",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainNodes.push({
                id: retriesFalseId,
                type: "ivrBlock",
                position: branch.falseNode,
                data: {
                    label: "false",
                    shape: "square",
                    icon: "pi pi-times",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainEdges.push(makeEdge(childId, retriesId));
            chainEdges.push(makeEdge(retriesId, retriesTrueId));
            chainEdges.push(makeEdge(retriesId, retriesFalseId));
        } else if (label === "Others" && menuRetries != null) {
            const retriesId = `${chainPrefix}others-retries`;
            const retriesTrueId = `${chainPrefix}others-retries-true`;
            const retriesFalseId = `${chainPrefix}others-retries-false`;
            const branch = ivrMenuRetriesBranchLayout(x, dtmfChildY);
            chainNodes.push({
                id: retriesId,
                type: "ivrBlock",
                position: branch.retries,
                data: {
                    label: `Retries\n${menuRetries}`,
                    shape: "square",
                    icon: "pi pi-refresh",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainNodes.push({
                id: retriesTrueId,
                type: "ivrBlock",
                position: branch.trueNode,
                data: {
                    label: "true",
                    shape: "square",
                    icon: "pi pi-check",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainNodes.push({
                id: retriesFalseId,
                type: "ivrBlock",
                position: branch.falseNode,
                data: {
                    label: "false",
                    shape: "square",
                    icon: "pi pi-times",
                    accentColor: configAccentColor,
                    isGenerated: true,
                    configParentId: configNodeId,
                },
            });
            chainEdges.push(makeEdge(childId, retriesId));
            chainEdges.push(makeEdge(retriesId, retriesTrueId));
            chainEdges.push(makeEdge(retriesId, retriesFalseId));
        }
    }

    return { nodes: chainNodes, edges: chainEdges };
}

function expandPlayChain(
    configNodeId: string,
    config: Record<string, unknown>,
    configX: number,
    configY: number,
    configAccentColor: string
): { nodes: Node<HydrationBlockData>[]; edges: Edge[] } {
    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const chainNodes: Node<HydrationBlockData>[] = [];
    const chainEdges: Edge[] = [];
    const meta = (config.playAudioMeta as { audioId: number; audioName: string }[]) ?? [];
    const silensPre = config.playSilensPre as number | null;
    const silensPost = config.playSilensPost as number | null;
    const playTimeout = config.playTimeout as number | null;

    let lastSourceId = configNodeId;
    let y = configY;

    if (silensPre != null) {
        const preId = `${chainPrefix}silens-pre`;
        y += IVR_PLAY_CHAIN_VERTICAL_GAP;
        chainNodes.push({
            id: preId,
            type: "ivrBlock",
            position: { x: configX, y },
            data: {
                label: `Silens Pre\n${silensPre}`,
                shape: "square",
                icon: "pi pi-clock",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(lastSourceId, preId));
        lastSourceId = preId;
    }

    for (let i = 0; i < meta.length; i++) {
        const audioNodeId = `${chainPrefix}audio-${i}`;
        y += IVR_PLAY_CHAIN_VERTICAL_GAP;
        chainNodes.push({
            id: audioNodeId,
            type: "ivrBlock",
            position: { x: configX, y },
            data: {
                label: wrapLabelToLines(meta[i].audioName, 18, 3),
                shape: "triangle",
                icon: "pi pi-play",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(lastSourceId, audioNodeId));
        lastSourceId = audioNodeId;
    }

    if (silensPost != null) {
        const postId = `${chainPrefix}silens-post`;
        y += IVR_PLAY_CHAIN_VERTICAL_GAP;
        chainNodes.push({
            id: postId,
            type: "ivrBlock",
            position: { x: configX, y },
            data: {
                label: `Silens Post\n${silensPost}`,
                shape: "square",
                icon: "pi pi-clock",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(lastSourceId, postId));
        lastSourceId = postId;
    }

    if (playTimeout != null) {
        const timeoutId = `${chainPrefix}timeout`;
        y += IVR_PLAY_CHAIN_VERTICAL_GAP;
        chainNodes.push({
            id: timeoutId,
            type: "ivrBlock",
            position: { x: configX, y },
            data: {
                label: `Timeout\n${playTimeout}`,
                shape: "square",
                icon: "pi pi-clock",
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
            },
        });
        chainEdges.push(makeEdge(lastSourceId, timeoutId));
    }

    return { nodes: chainNodes, edges: chainEdges };
}

function expandOperatorQueueChain(
    configNodeId: string,
    config: Record<string, unknown>,
    configX: number,
    configY: number,
    configAccentColor: string
): { nodes: Node<HydrationBlockData>[]; edges: Edge[] } {
    const qids =
        ((config.hunterQueueIds as number[] | undefined) ?? []).filter(
            (n) => Number.isFinite(n) && n > 0
        );
    if (qids.length === 0) {
        return { nodes: [], edges: [] };
    }

    const dummyId = ivrOperatorQueueChainNodeId(configNodeId);
    const qId = qids[0]!;
    const meta =
        (config.hunterQueueMeta as
            | { queueId: number; queueName: string }[]
            | undefined) ?? [];
    const queueName = meta.find((m) => m.queueId === qId)?.queueName;
    const label =
        queueName != null && String(queueName).trim() !== ""
            ? `ID ${qId}\n${String(queueName).trim()}\nPlayBack`
            : `ID ${qId}\nQueue\nPlayBack`;

    const y = ivrAgentHunterQueueChildY(configY, IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT);
    const node: Node<HydrationBlockData> = {
        id: dummyId,
        type: "ivrBlock",
        position: { x: configX, y },
        data: {
            label,
            shape: "square",
            icon: "pi pi-database",
            accentColor: configAccentColor,
            isGenerated: true,
            configParentId: configNodeId,
        },
    };

    return {
        nodes: [node],
        edges: [makeEdge(configNodeId, dummyId)],
    };
}

function routeToMenuChainSourceId(
    configNodeId: string,
    chainPrefix: string,
    config: Record<string, unknown>,
    route: IvrApiRoute
): string | null {
    const menuTimeout = config.menuTimeout as number | null;
    const menuTimeoutRetries = config.menuTimeoutRetries as number | null;
    const menuRetries = config.menuRetries as number | null;
    const menuDtmfKeys = (config.menuDtmfKeys as string[] | undefined) ?? [];
    const menuDtmfRetries =
        (config.menuDtmfRetries as
            | Record<string, number | null | undefined>
            | undefined) ?? {};
    const allDtmfSelected = menuDtmfKeys.length >= ALL_DTMF_KEYS.length;
    const raw = (route.routeValue ?? "").trim();
    const nt = normalizeRouteType(route.routeType);
    const ev = normalizeEventRoute(raw);

    const dtmfKeySource = (key: string, exceeded: boolean): string | null => {
        if (!ALL_DTMF_KEYS.includes(key)) return null;
        const hasPerKeyRetries = menuDtmfRetries[key] != null;
        if (exceeded) {
            if (hasPerKeyRetries) return `${chainPrefix}retries-${key}-false`;
            return `${chainPrefix}dtmf-${key}`;
        }
        if (hasPerKeyRetries) return `${chainPrefix}retries-${key}-true`;
        return `${chainPrefix}dtmf-${key}`;
    };

    if (nt === "DEFAULT") {
        if (raw !== "") return null;
        return menuTailIdBeforeDtmf(configNodeId, chainPrefix, config);
    }

    if (nt === "DTMF") {
        return dtmfKeySource(raw, false);
    }

    if (nt === "DTMF_EXCEEDED") {
        return dtmfKeySource(raw, true);
    }

    if (nt === "EVENT") {
        if (ev === "timeout" && menuTimeout != null) {
            if (menuTimeoutRetries != null) {
                return `${chainPrefix}timeout-retries-true`;
            }
            return `${chainPrefix}dtmf-timeout`;
        }
        if (ev === "invalid" && !allDtmfSelected) {
            if (menuRetries != null) {
                return `${chainPrefix}others-retries-true`;
            }
            return `${chainPrefix}dtmf-others`;
        }
        return null;
    }

    if (nt === "EVENT_EXCEEDED") {
        if (ev === "timeout" && menuTimeout != null) {
            if (menuTimeoutRetries != null) {
                return `${chainPrefix}timeout-retries-false`;
            }
            return `${chainPrefix}dtmf-timeout`;
        }
        if (ev === "invalid" && !allDtmfSelected) {
            if (menuRetries != null) {
                return `${chainPrefix}others-retries-false`;
            }
            return `${chainPrefix}dtmf-others`;
        }
        return null;
    }

    if (nt === "") {
        if (raw === "") {
            return menuTailIdBeforeDtmf(configNodeId, chainPrefix, config);
        }
        if (ev === "timeout" && menuTimeout != null) {
            if (menuTimeoutRetries != null) {
                return `${chainPrefix}timeout-retries-true`;
            }
            return `${chainPrefix}dtmf-timeout`;
        }
        if (ev === "invalid" && !allDtmfSelected) {
            if (menuRetries != null) {
                return `${chainPrefix}others-retries-true`;
            }
            return `${chainPrefix}dtmf-others`;
        }
        if (ALL_DTMF_KEYS.includes(raw)) {
            return dtmfKeySource(raw, false);
        }
        return null;
    }

    return null;
}

function playChainTailId(
    configNodeId: string,
    chainPrefix: string,
    config: Record<string, unknown>
): string {
    const meta = (config.playAudioMeta as { audioId: number; audioName: string }[]) ?? [];
    const silensPre = config.playSilensPre as number | null;
    const silensPost = config.playSilensPost as number | null;
    const playTimeout = config.playTimeout as number | null;
    if (playTimeout != null) return `${chainPrefix}timeout`;
    if (silensPost != null) return `${chainPrefix}silens-post`;
    if (meta.length > 0) return `${chainPrefix}audio-${meta.length - 1}`;
    if (silensPre != null) return `${chainPrefix}silens-pre`;
    return configNodeId;
}

function routeToPlayChainSourceId(
    configNodeId: string,
    chainPrefix: string,
    config: Record<string, unknown>,
    route: IvrApiRoute
): string {
    const raw = (route.routeValue ?? "").trim();
    const nt = normalizeRouteType(route.routeType);
    const ev = normalizeEventRoute(raw);
    const tail = playChainTailId(configNodeId, chainPrefix, config);

    if (nt === "EVENT") {
        if (ev === "timeout" && config.playTimeout != null) {
            return `${chainPrefix}timeout`;
        }
        return tail;
    }

    if (nt === "EVENT_EXCEEDED") {
        if (ev === "timeout" && config.playTimeout != null) {
            return `${chainPrefix}timeout`;
        }
        return tail;
    }

    return tail;
}

function collectBfsOrder(
    entryElementId: number,
    elementsById: Map<number, IvrApiElement>,
    routes: IvrApiRoute[],
    schedules?: IvrApiSchedule[]
): IvrApiElement[] {
    const routesByFrom = new Map<number, IvrApiRoute[]>();
    for (const r of routes) {
        if (r.isActive === false) continue;
        const list = routesByFrom.get(r.fromElementId) ?? [];
        list.push(r);
        routesByFrom.set(r.fromElementId, list);
    }
    routesByFrom.forEach((list) => {
        list.sort(
            (a: IvrApiRoute, b: IvrApiRoute) =>
                (a.priority ?? 0) - (b.priority ?? 0)
        );
    });

    const ordered: IvrApiElement[] = [];
    const visited = new Set<number>();
    const queue: number[] = [entryElementId];

    while (queue.length) {
        const id = queue.shift()!;
        if (visited.has(id)) continue;
        visited.add(id);
        const el = elementsById.get(id);
        if (!el) continue;
        ordered.push(el);
        const outs = routesByFrom.get(id) ?? [];
        for (const r of outs) {
            if (!visited.has(r.toElementId)) queue.push(r.toElementId);
        }
        for (const s of schedules ?? []) {
            if (!ivrRawScheduleIsActive(s)) continue;
            if (ivrRawScheduleElementId(s) !== id) continue;
            const tid = ivrRawScheduleActiveToId(s);
            if (tid !== 0 && !visited.has(tid)) queue.push(tid);
        }
    }
    const orphanIds = Array.from(elementsById.keys())
        .filter((k) => !visited.has(k))
        .sort((a, b) => a - b);
    for (const oid of orphanIds) {
        const el = elementsById.get(oid);
        if (el) ordered.push(el);
    }
    return ordered;
}

function schedulerTreeLayoutChildIds(
    elementId: number,
    config: Record<string, unknown>,
    schedules: IvrApiSchedule[] | undefined,
    elementsById: Map<number, IvrApiElement>
): number[] {
    const kind = normalizeIvrSchedulerScheduleKind(
        config.schedulerScheduleKind
    );
    const out: number[] = [];
    const seen = new Set<number>();

    const push = (tid: number) => {
        if (
            tid === 0 ||
            seen.has(tid) ||
            !elementsById.has(tid)
        ) {
            return;
        }
        seen.add(tid);
        out.push(tid);
    };

    if (kind === "once") {
        const rawAid = config.schedulerActiveToId;
        const aid = typeof rawAid === "number" ? rawAid : Number(rawAid);
        if (Number.isFinite(aid) && aid > 0) push(aid);
        for (const s of schedules ?? []) {
            if (!ivrRawScheduleIsActive(s)) continue;
            if (ivrRawScheduleElementId(s) !== elementId) continue;
            push(ivrRawScheduleActiveToId(s));
        }
        return out;
    }

    const sr =
        (config.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ?? [];
    for (const row of sr) {
        push(row.activeToId);
    }
    return out;
}

export function resolveEffectiveIvrEntryElementId(
    raw: number | null | undefined,
    elements: IvrApiElement[]
): number {
    if (!elements?.length) return 0;
    const n = raw != null ? Number(raw) : NaN;
    if (
        Number.isFinite(n) &&
        n > 0 &&
        elements.some((e) => Number(e.elementId) === n)
    ) {
        return n;
    }
    const first = elements.find(
        (e) => (e.elementType ?? "").toUpperCase() !== "END"
    )?.elementId;
    if (first != null) {
        const f = Number(first);
        if (Number.isFinite(f) && f > 0) return f;
    }
    const a = Number(elements[0]?.elementId);
    return Number.isFinite(a) ? a : 0;
}

export type HydratedGraph = {
    nodes: Node<HydrationBlockData>[];
    edges: Edge[];
    nextStartConfigId: number;
    elementIdToConfigNodeId: Map<number, string>;
};

export function buildHydratedGraphFromApiPayload(params: {
    entryElementId: number | null | undefined;
    elements: IvrApiElement[];
    routes: IvrApiRoute[];
    schedules?: IvrApiSchedule[];
}): HydratedGraph | null {
    const { elements, routes, schedules } = params;
    const entryElementId = resolveEffectiveIvrEntryElementId(
        params.entryElementId,
        elements
    );

    if (!elements?.length) return null;

    const elementsById = new Map(elements.map((e) => [e.elementId, e]));
    const ordered = collectBfsOrder(
        entryElementId,
        elementsById,
        routes,
        schedules
    );
    if (!ordered.length) return null;

    const elementIdToConfigNodeId = new Map<number, string>();
    ordered.forEach((el, i) => {
        elementIdToConfigNodeId.set(el.elementId, `${START_CONFIG_PREFIX}${i + 1}`);
    });

    const treeChildren = new Map<number, number[]>();
    {
        const tv = new Set<number>();
        const tq: number[] = [entryElementId];
        tv.add(entryElementId);
        while (tq.length) {
            const id = tq.shift()!;
            const ch: number[] = [];
            const el = elementsById.get(id);
            if (el) {
                const c = elementToBlockConfig(el, routes, schedules);
                const tp = (c.startConfiguration as string) || "Play";
                if (tp === "Scheduler") {
                    for (const tid of schedulerTreeLayoutChildIds(
                        id,
                        c,
                        schedules,
                        elementsById
                    )) {
                        if (tv.has(tid)) continue;
                        tv.add(tid);
                        ch.push(tid);
                        tq.push(tid);
                    }
                }
                for (const r of routesFromElementSorted(id, routes)) {
                    if (!tv.has(r.toElementId) && elementsById.has(r.toElementId)) {
                        tv.add(r.toElementId);
                        ch.push(r.toElementId);
                        tq.push(r.toElementId);
                    }
                }
                if (tp !== "Scheduler") {
                    for (const s of schedules ?? []) {
                        if (!ivrRawScheduleIsActive(s)) continue;
                        if (ivrRawScheduleElementId(s) !== id) continue;
                        const tid = ivrRawScheduleActiveToId(s);
                        if (
                            tid !== 0 &&
                            !tv.has(tid) &&
                            elementsById.has(tid)
                        ) {
                            tv.add(tid);
                            ch.push(tid);
                            tq.push(tid);
                        }
                    }
                }
            }
            treeChildren.set(id, ch);
        }
        for (const el of ordered) {
            if (!treeChildren.has(el.elementId))
                treeChildren.set(el.elementId, []);
        }
    }

    const TREE_COL_GAP = 80;
    const TREE_MIN_W = 240;

    function treeElWidth(eid: number): number {
        const el = elementsById.get(eid);
        if (!el) return TREE_MIN_W;
        const c = elementToBlockConfig(el, routes, schedules);
        const t = (c.startConfiguration as string) || "Play";
        if (t === "Menu") {
            const dk = (c.menuDtmfKeys as string[] | undefined) ?? [];
            let n = dk.length;
            if (dk.length < ALL_DTMF_KEYS.length) n++;
            if (c.menuTimeout != null) n++;
            return Math.max(TREE_MIN_W, Math.max(0, n - 1) * 152 + 180);
        }
        if (t === "Scheduler") {
            const schedKind = normalizeIvrSchedulerScheduleKind(
                c.schedulerScheduleKind
            );
            if (schedKind === "once") {
                const w = resolveSchedulerOnceExtraWindow(c);
                const fullDay =
                    w?.start?.trim() && w?.end?.trim()
                        ? schedulerOnceDailyIsFullDay(w.start, w.end)
                        : true;
                const n = fullDay ? 1 : 2;
                const no = !!c.schedulerShowOthers;
                const slotCount = n + (no ? 1 : 0);
                return Math.max(
                    TREE_MIN_W,
                    Math.max(0, slotCount - 1) * IVR_SCHEDULER_SIBLING_GAP_PX +
                        IVR_SCHEDULER_SIBLING_GAP_PX
                );
            }
            const sr =
                (c.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ?? [];
            const no = schedulerChainNeedsOthers(sr, el.elementId, routes);
            const n = sr.length + (no ? 1 : 0);
            return Math.max(TREE_MIN_W, Math.max(0, n - 1) * IVR_SCHEDULER_SIBLING_GAP_PX + IVR_SCHEDULER_SIBLING_GAP_PX);
        }
        return TREE_MIN_W;
    }

    function treeChainH(eid: number): number {
        const el = elementsById.get(eid);
        if (!el) return 120;
        const c = elementToBlockConfig(el, routes, schedules);
        const t = (c.startConfiguration as string) || "Play";
        if (t === "Menu") {
            return ivrEstimateMenuChainHeight(c as Record<string, unknown>);
        }
        if (t === "Play") {
            const ac = ((c.playAudioMeta as unknown[]) ?? []).length;
            const lv =
                (c.playSilensPre != null ? 1 : 0) +
                ac +
                (c.playSilensPost != null ? 1 : 0) +
                (c.playTimeout != null ? 1 : 0);
            return Math.max(1, lv) * IVR_PLAY_CHAIN_VERTICAL_GAP + 120;
        }
        if (t === "Scheduler") {
            const schedKind = normalizeIvrSchedulerScheduleKind(
                c.schedulerScheduleKind
            );
            const slotRows =
                (c.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ?? [];
            const useAdvancedTree =
                schedKind === "daysweek_advanced" &&
                slotRows.length > 0 &&
                !schedulerPreferFlatSlotRowChain(slotRows);
            const rowLevels = useAdvancedTree ? 2 : 1;
            return (
                IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT +
                rowLevels *
                    (IVR_TREE_TRUNK_TO_BUS_GAP +
                        IVR_TREE_BUS_TO_CHILD_DROP_PX +
                        IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT) +
                IVR_TREE_PARENT_TO_CHILD_GAP
            );
        }
        if (t === "AgentHunter")
            return (
                IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT +
                IVR_AGENT_HUNTER_QUEUE_VERTICAL_GAP +
                IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT +
                IVR_TREE_PARENT_TO_CHILD_GAP
            );
        return IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT + IVR_TREE_PARENT_TO_CHILD_GAP;
    }

    const stWCache = new Map<number, number>();
    function stW(eid: number): number {
        if (stWCache.has(eid)) return stWCache.get(eid)!;
        const ow = treeElWidth(eid);
        const ch = treeChildren.get(eid) ?? [];
        let w: number;
        if (ch.length === 0) w = ow;
        else if (ch.length === 1) w = Math.max(ow, stW(ch[0]));
        else
            w = Math.max(
                ow,
                ch.reduce((s, c) => s + stW(c), 0) +
                    (ch.length - 1) * TREE_COL_GAP
            );
        stWCache.set(eid, w);
        return w;
    }

    const treePos = new Map<number, { x: number; y: number }>();
    function assignPos(eid: number, cx: number, cy: number): void {
        treePos.set(eid, { x: cx, y: cy });
        const ch = treeChildren.get(eid) ?? [];
        if (!ch.length) return;
        const ny = cy + treeChainH(eid);
        if (ch.length === 1) {
            assignPos(ch[0], cx, ny);
            return;
        }
        const tw =
            ch.reduce((s, c) => s + stW(c), 0) +
            (ch.length - 1) * TREE_COL_GAP;
        let sx = cx - tw / 2;
        for (const c of ch) {
            const cw = stW(c);
            assignPos(c, sx + cw / 2, ny);
            sx += cw + TREE_COL_GAP;
        }
    }

    assignPos(entryElementId, stW(entryElementId) / 2 + 80, 160);

    let orphY = 160;
    treePos.forEach((p, eid) => {
        orphY = Math.max(orphY, p.y + treeChainH(eid));
    });
    for (const el of ordered) {
        if (!treePos.has(el.elementId)) {
            treePos.set(el.elementId, { x: 80, y: orphY });
            orphY += treeChainH(el.elementId) + IVR_TREE_PARENT_TO_CHILD_GAP;
        }
    }
    let minXP = Infinity;
    treePos.forEach((p) => {
        if (p.x < minXP) minXP = p.x;
    });
    if (minXP < 80) {
        const off = 80 - minXP;
        treePos.forEach((p) => {
            p.x += off;
        });
    }
    const entryP = treePos.get(entryElementId) ?? { x: 80, y: 160 };
    const nodes: Node<HydrationBlockData>[] = [
        {
            id: BASE_NODE_ID,
            type: "ivrBlock",
            position: {
                x: entryP.x,
                y: entryP.y - IVR_START_ABOVE_ENTRY_GAP - IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT,
            },
            data: {
                label: "Start",
                shape: "circle",
                icon: "pi pi-circle-fill",
            },
        },
    ];
    const edges: Edge[] = [];

    for (let i = 0; i < ordered.length; i++) {
        const el = ordered[i];
        const configNodeId = elementIdToConfigNodeId.get(el.elementId)!;

        const blockConfig = elementToBlockConfig(el, routes, schedules);
        const configType = (blockConfig.startConfiguration as string) || "Play";
        const { configLabel, configIcon, configAccentColor } = configPresentation(
            configType,
            (blockConfig.startConfigTitle as string) ?? ""
        );

        const tp = treePos.get(el.elementId);
        const configX = tp?.x ?? 80;
        const configY = tp?.y ?? 160;

        const cfgWithSource: Record<string, unknown> = { ...blockConfig };

        nodes.push({
            id: configNodeId,
            type: "ivrBlock",
            position: { x: configX, y: configY },
            data: {
                label: configLabel,
                shape: "circle",
                icon: configIcon,
                accentColor: configAccentColor,
                isGenerated: true,
                configParentId: configNodeId,
                ...(configType !== "End"
                    ? { configSourceNodeId: BASE_NODE_ID }
                    : {}),
                blockConfig: cfgWithSource,
            },
        });

        if (configType === "Menu") {
            const { nodes: cn, edges: ce } = expandMenuChain(
                configNodeId,
                blockConfig,
                configX,
                configY,
                configAccentColor
            );
            nodes.push(...cn);
            edges.push(...ce);
        } else if (configType === "Play") {
            const { nodes: cn, edges: ce } = expandPlayChain(
                configNodeId,
                blockConfig,
                configX,
                configY,
                configAccentColor
            );
            nodes.push(...cn);
            edges.push(...ce);
        } else if (configType === "Scheduler") {
            const schedKind = normalizeIvrSchedulerScheduleKind(
                blockConfig.schedulerScheduleKind
            );
            if (schedKind === "once") {
                const { nodes: cn, edges: ce } = expandSchedulerOnceDailyChain(
                    configNodeId,
                    blockConfig,
                    configX,
                    configY,
                    configAccentColor
                );
                nodes.push(...cn);
                edges.push(...ce);
            } else if (schedKind === "daysweek") {
                const { nodes: cn, edges: ce } =
                    expandSchedulerDaysweekSimpleChain(
                        configNodeId,
                        blockConfig,
                        configX,
                        configY,
                        configAccentColor,
                        { elementId: el.elementId, routes }
                    );
                nodes.push(...cn);
                edges.push(...ce);
            } else {
                const slotRows =
                    (blockConfig.schedulerSlotRows as IvrSchedulerSlotRow[]) ??
                    [];
                if (slotRows.length > 0) {
                    const schedKindSlots = normalizeIvrSchedulerScheduleKind(
                        blockConfig.schedulerScheduleKind
                    );
                    const useAdvancedTree =
                        schedKindSlots === "daysweek_advanced" &&
                        !schedulerPreferFlatSlotRowChain(slotRows);
                    const expandSlots = useAdvancedTree
                        ? expandSchedulerDaysweekAdvancedChain
                        : expandSchedulerSlotChain;
                    const { nodes: cn, edges: ce } = expandSlots(
                        configNodeId,
                        blockConfig,
                        configX,
                        configY,
                        configAccentColor,
                        { elementId: el.elementId, routes }
                    );
                    nodes.push(...cn);
                    edges.push(...ce);
                }
            }
        } else if (configType === "AgentHunter") {
            const qids =
                ((blockConfig.hunterQueueIds as number[] | undefined) ?? [])
                    .filter((n) => Number.isFinite(n) && n > 0);
            if (qids.length > 0) {
                const { nodes: cn, edges: ce } = expandOperatorQueueChain(
                    configNodeId,
                    blockConfig,
                    configX,
                    configY,
                    configAccentColor
                );
                nodes.push(...cn);
                edges.push(...ce);
            }
        }
    }

    const incomingSourceByTarget = new Map<number, string>();
    const sortedRoutes = [...routes]
        .filter((r) => r.isActive !== false)
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

    for (const r of sortedRoutes) {
        const fromEl = elementsById.get(r.fromElementId);
        const toConfigId = elementIdToConfigNodeId.get(r.toElementId);
        const fromConfigId = elementIdToConfigNodeId.get(r.fromElementId);
        if (!fromEl || !toConfigId || !fromConfigId) continue;

        const fromCfg = elementToBlockConfig(fromEl, routes, schedules);
        const fromType = (fromCfg.startConfiguration as string) || "Play";
        const chainP = `${CHAIN_PREFIX}${fromConfigId}-`;

        const slotRows =
            (fromCfg.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ??
            [];

        let sourceNodeId: string | null = null;
        if (fromType === "Scheduler" && slotRows.length > 0) {
            const sortedSlots = [...slotRows].sort(
                (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
            );
            const schedKind = normalizeIvrSchedulerScheduleKind(
                fromCfg.schedulerScheduleKind
            );
            if (schedKind === "once") {
                sourceNodeId = resolveOnceSchedulerRouteChainSourceId(
                    chainP,
                    fromCfg,
                    sortedSlots,
                    r,
                    routes,
                    fromEl.elementId
                );
                if (!sourceNodeId) continue;
            } else if (schedKind === "daysweek") {
                sourceNodeId = resolveDaysweekSimpleSchedulerRouteChainSourceId(
                    chainP,
                    fromCfg,
                    sortedSlots,
                    r
                );
                if (!sourceNodeId) continue;
            } else {
                sourceNodeId = resolveSlotSchedulerRouteChainSourceId(
                    chainP,
                    sortedSlots,
                    r,
                    routes,
                    fromEl.elementId
                );
                if (!sourceNodeId) continue;
            }
        } else if (fromType === "Menu") {
            sourceNodeId = routeToMenuChainSourceId(
                fromConfigId,
                chainP,
                fromCfg,
                r
            );
            if (!sourceNodeId) {
                const ntR = normalizeRouteType(r.routeType);
                const rvEmpty = (r.routeValue ?? "").trim() === "";
                if (
                    rvEmpty &&
                    (ntR === "DEFAULT" ||
                        ntR === "" ||
                        String(r.routeType ?? "").trim() === "")
                ) {
                    sourceNodeId = menuTailIdBeforeDtmf(
                        fromConfigId,
                        chainP,
                        fromCfg
                    );
                }
            }
            if (!sourceNodeId) continue;
        } else if (fromType === "Play") {
            sourceNodeId = routeToPlayChainSourceId(
                fromConfigId,
                chainP,
                fromCfg,
                r
            );
        } else if (fromType === "AgentHunter") {
            const qids =
                ((fromCfg.hunterQueueIds as number[] | undefined) ?? []).filter(
                    (n) => Number.isFinite(n) && n > 0
                );
            sourceNodeId =
                qids.length > 0
                    ? ivrOperatorQueueChainNodeId(fromConfigId)
                    : fromConfigId;
        } else if (fromType === "End") {
            continue;
        } else {
            sourceNodeId = fromConfigId;
        }

        const baseEdge = makeEdge(sourceNodeId, toConfigId, "bottom-source", "top", {
            ivrRoute: { ...r },
        });
        const schedOthersSource =
            fromType === "Scheduler" &&
            slotRows.length > 0 &&
            sourceNodeId?.endsWith("sched-others");
        const edge = schedOthersSource
            ? {
                  ...baseEdge,
                  id: `${baseEdge.id}-rte-${r.id ?? `${r.priority ?? 0}-${r.toElementId}`}`,
              }
            : baseEdge;
        edges.push(edge);

        const menuTailBeforeDtmf =
            fromType === "Menu"
                ? menuTailIdBeforeDtmf(fromConfigId, chainP, fromCfg)
                : null;
        const playLinearTail =
            fromType === "Play"
                ? playChainTailId(fromConfigId, chainP, fromCfg)
                : null;
        const preferLinearTailIncoming =
            (fromType === "Menu" &&
                menuTailBeforeDtmf != null &&
                sourceNodeId === menuTailBeforeDtmf) ||
            (fromType === "Play" &&
                playLinearTail != null &&
                sourceNodeId === playLinearTail);
        if (preferLinearTailIncoming) {
            incomingSourceByTarget.set(r.toElementId, sourceNodeId);
        } else if (!incomingSourceByTarget.has(r.toElementId)) {
            incomingSourceByTarget.set(r.toElementId, sourceNodeId);
        }
    }

    for (const el of ordered) {
        const cfg = elementToBlockConfig(el, routes, schedules);
        if ((cfg.startConfiguration as string) !== "Scheduler") continue;
        const slots =
            (cfg.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ?? [];
        if (!slots.length) continue;
        const fromConfigId = elementIdToConfigNodeId.get(el.elementId)!;
        const chainP = `${CHAIN_PREFIX}${fromConfigId}-`;
        const sorted = [...slots].sort(
            (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
        );
        const schedKind = normalizeIvrSchedulerScheduleKind(
            cfg.schedulerScheduleKind
        );

        const elSchedules = schedulesForElement(el.elementId, schedules);
        for (const sched of elSchedules) {
            const targetId = ivrRawScheduleActiveToId(sched);
            if (targetId === 0) continue;
            const toCfg = elementIdToConfigNodeId.get(targetId);
            if (!toCfg) continue;

            const sr = sched as Record<string, unknown>;
            const rowFromSched: IvrSchedulerSlotRow = {
                ...(ivrRawScheduleRowId(sched) > 0
                    ? { id: ivrRawScheduleRowId(sched) }
                    : {}),
                priority: ivrRawSchedulePriority(sched) || 0,
                daysMask: Number(sr.daysMask ?? sr.DaysMask) || 0,
                start: trimTimeToHHMM(
                    String(sr.fromTime ?? sr.FromTime ?? "")
                ),
                end: trimTimeToHHMM(String(sr.toTime ?? sr.ToTime ?? "")),
                activeToId: targetId,
            };

            const idx = sorted.findIndex(
                (r) =>
                    (Number(r.daysMask) || 0) === rowFromSched.daysMask &&
                    trimTimeToHHMM(String(r.start ?? "")) ===
                        rowFromSched.start &&
                    trimTimeToHHMM(String(r.end ?? "")) === rowFromSched.end
            );
            const slotsForResolve =
                sorted.length > 0 ? sorted : [rowFromSched];

            let slotNodeId: string | null = null;
            if (schedKind === "once") {
                slotNodeId = resolveOnceSchedulerChainNodeForRow(
                    chainP,
                    cfg,
                    rowFromSched,
                    slotsForResolve
                );
            } else if (schedKind === "daysweek") {
                const meta = DAYS_MASK_ORDER.find(
                    (d) => d.mask === rowFromSched.daysMask
                );
                if (meta) {
                    slotNodeId = `${chainP}day-${meta.dayOfWeek}`;
                }
            } else if (idx >= 0) {
                slotNodeId = `${chainP}sched-slot-${ivrSchedulerSlotNodeIdSuffix(
                    sorted,
                    idx
                )}`;
            } else if (slotsForResolve.length === 1) {
                slotNodeId = `${chainP}sched-slot-${ivrSchedulerSlotNodeIdSuffix(
                    sorted.length > 0 ? sorted : slotsForResolve,
                    0
                )}`;
            }

            if (!slotNodeId) continue;

            edges.push(makeEdge(slotNodeId, toCfg));
            incomingSourceByTarget.set(targetId, slotNodeId);
        }
    }

    for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (!n.id.startsWith(START_CONFIG_PREFIX)) continue;
        const el = ordered.find((e) => elementIdToConfigNodeId.get(e.elementId) === n.id);
        if (!el) continue;
        const src = incomingSourceByTarget.get(el.elementId);
        if (src == null) continue;
        const sourceForData =
            el.elementId === entryElementId ? BASE_NODE_ID : src;
        nodes[i] = {
            ...n,
            data: {
                ...n.data,
                configSourceNodeId: sourceForData,
                blockConfig: {
                    ...(n.data.blockConfig as object),
                    configSourceNodeId: sourceForData,
                },
            },
        };
    }

    for (const el of ordered) {
        const cfg = elementToBlockConfig(el, routes, schedules);
        const ct = (cfg.startConfiguration as string) || "Play";
        const configNodeId = elementIdToConfigNodeId.get(el.elementId)!;
        const attachSource =
            incomingSourceByTarget.get(el.elementId) ??
            (el.elementId === entryElementId ? BASE_NODE_ID : null);
        if (attachSource == null) continue;
        const chainEdgeId = `e-${attachSource}-${configNodeId}`;
        if (!edges.some((e) => e.id === chainEdgeId)) {
            edges.push(makeEdge(attachSource, configNodeId));
        }
        if (el.elementId === entryElementId) {
            const baseEntryEdgeId = `e-${BASE_NODE_ID}-${configNodeId}`;
            if (!edges.some((e) => e.id === baseEntryEdgeId)) {
                edges.push(makeEdge(BASE_NODE_ID, configNodeId));
            }
        }
    }

    return {
        nodes,
        edges,
        nextStartConfigId: ordered.length + 1,
        elementIdToConfigNodeId,
    };
}
